import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import {
  Plus, X, ChevronRight, CalendarDays, Users,
  DollarSign, CheckCircle2, Save, Trash2, Printer,
  ShoppingCart, ChevronDown, Link2, Check, History, User, UserRound, Eye, GripVertical,
  FileSpreadsheet,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '@/lib/supabase'
import { RezzAiPastePanel } from '@/components/ai/RezzAiPastePanel'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { PageSpinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/utils'
import {
  useCateringOrders, useSaveCateringOrder,
  useUpdateCateringStatus, useCateringPricing, useMarkCateringUpdateSeen,
  useCateringOrderRevisions,
} from '@/hooks/useCatering'
import {
  useMenuItems, useMenuCategories, groupMenuItemsByCategory,
} from '@/hooks/useMenu'
import { useTeamMembers } from '@/hooks/useTeam'
import { useAuthStore } from '@/stores/authStore'
import { CateringMenuMatrixSheet, type MatrixAddPayload } from '@/components/catering/CateringMenuMatrixSheet'
import { groupByCategory } from '@/hooks/usePublicCateringQuote'
import {
  CATERING_SECTIONS, CATERING_SECTION_LABELS,
  type CateringOrder, type CateringOrderItem,
  type TraySize, type CateringStatus, type MenuItem, type FulfillmentType, type CateringSectionKey,
} from '@/types/app'

// ── Status helpers ───────────────────────────────────────────────────────────
const STATUS_LABELS: Record<CateringStatus, string> = {
  inquiry:       'Inquiry',
  quoted:        'Quoted',
  deposit_paid:  'Deposit Paid',
  confirmed:     'Confirmed',
  in_production: 'In Production',
  delivered:     'Delivered',
  completed:     'Completed',
  cancelled:     'Cancelled',
}

const STATUS_VARIANTS: Record<CateringStatus, 'neutral'|'info'|'warning'|'success'|'danger'> = {
  inquiry:       'neutral',
  quoted:        'info',
  deposit_paid:  'warning',
  confirmed:     'info',
  in_production: 'warning',
  delivered:     'success',
  completed:     'success',
  cancelled:     'danger',
}

const STATUS_FLOW: Partial<Record<CateringStatus, { next: CateringStatus; label: string }>> = {
  inquiry:       { next: 'quoted',        label: 'Mark as Quoted' },
  quoted:        { next: 'confirmed',     label: 'Confirm Order' },
  deposit_paid:  { next: 'confirmed',     label: 'Confirm Order' },
  confirmed:     { next: 'in_production', label: 'Start Production' },
  in_production: { next: 'delivered',     label: 'Mark Delivered' },
  delivered:     { next: 'completed',     label: 'Mark Completed' },
}

// Canonical lifecycle steps shown in the stepper (deposit_paid is a payment event, not a stage)
const LIFECYCLE_STEPS: { status: CateringStatus; label: string }[] = [
  { status: 'inquiry',       label: 'Inquiry' },
  { status: 'quoted',        label: 'Quoted' },
  { status: 'confirmed',     label: 'Confirmed' },
  { status: 'in_production', label: 'In Prod.' },
  { status: 'delivered',     label: 'Delivered' },
  { status: 'completed',     label: 'Done' },
]

function stepIndex(status: CateringStatus): number {
  // deposit_paid sits between quoted and confirmed — treat as confirmed for display
  const s = status === 'deposit_paid' ? 'confirmed' : status
  return LIFECYCLE_STEPS.findIndex(st => st.status === s)
}

function LifecycleStepper({ status }: { status: CateringStatus }) {
  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-1.5 text-sm text-danger font-medium">
        <X size={14} /> Order Cancelled
      </div>
    )
  }
  const current = stepIndex(status)
  return (
    <div className="flex items-center gap-0">
      {LIFECYCLE_STEPS.map((step, i) => {
        const done    = i < current
        const active  = i === current
        const future  = i > current
        return (
          <div key={step.status} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={[
                'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors',
                done   ? 'bg-brand-600 border-brand-600 text-white'
                : active ? 'bg-white border-brand-600 text-brand-600'
                : 'bg-white border-stone-300 text-stone-400',
              ].join(' ')}>
                {done ? '✓' : i + 1}
              </div>
              <span className={[
                'text-2xs mt-0.5 whitespace-nowrap font-medium',
                done || active ? 'text-brand-700' : 'text-stone-400',
              ].join(' ')}>
                {step.label}
              </span>
            </div>
            {i < LIFECYCLE_STEPS.length - 1 && (
              <div className={[
                'h-0.5 w-6 sm:w-8 mb-3 mx-0.5',
                i < current ? 'bg-brand-600' : 'bg-stone-200',
              ].join(' ')} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// Live, not saved — computed from the exact same form/items state as the
// breakdown card below it, so the two can never drift apart the way "Quote:
// $X" (from the last save) vs. a live-recalculated total below it used to.
// The bolded "Quote" figure is always tax-INCLUSIVE — what the customer
// actually owes, and what Advance/Balance split from — never the pre-tax
// subtotal (splitting that undercounted every payment by the tax amount).
function PaymentSummary({
  total, subtotalBeforeTax, depositAmount, depositPaidAt, balanceAmount, balancePaidAt,
}: {
  total: number
  subtotalBeforeTax: number
  depositAmount: number
  depositPaidAt: string
  balanceAmount: number
  balancePaidAt: string
}) {
  const depPaid = !!depositPaidAt
  const balPaid = !!balancePaidAt
  const outstanding = total - (depPaid ? depositAmount : 0) - (balPaid ? balanceAmount : 0)

  if (total === 0) return null
  return (
    <div className="flex flex-wrap gap-3 px-6 py-3 bg-stone-50 border-b border-stone-200 text-sm">
      <div className="flex items-center gap-1.5">
        <span className="text-stone-500">Quote:</span>
        <span className="font-semibold text-stone-900">${total.toFixed(2)}</span>
        <span className="text-2xs text-stone-400">(${subtotalBeforeTax.toFixed(2)} before tax)</span>
      </div>
      <div className="w-px bg-stone-300 self-stretch" />
      <div className="flex items-center gap-1.5">
        {depPaid
          ? <CheckCircle2 size={14} className="text-success-dark" />
          : <div className="w-3.5 h-3.5 rounded-full border-2 border-stone-300" />}
        <span className={depPaid ? 'text-success-dark font-medium' : 'text-stone-500'}>
          Advance ${depositAmount.toFixed(2)}
          {depPaid ? ` · paid ${depositPaidAt.split('T')[0]}` : ' · pending'}
        </span>
      </div>
      <div className="w-px bg-stone-300 self-stretch" />
      <div className="flex items-center gap-1.5">
        {balPaid
          ? <CheckCircle2 size={14} className="text-success-dark" />
          : <div className="w-3.5 h-3.5 rounded-full border-2 border-stone-300" />}
        <span className={balPaid ? 'text-success-dark font-medium' : 'text-stone-500'}>
          Balance ${balanceAmount.toFixed(2)}
          {balPaid ? ` · paid ${balancePaidAt.split('T')[0]}` : ' · pending'}
        </span>
      </div>
      {outstanding > 0.005 && (
        <>
          <div className="w-px bg-stone-300 self-stretch" />
          <div className="flex items-center gap-1 text-warning-dark font-semibold">
            <DollarSign size={13} /> ${outstanding.toFixed(2)} outstanding
          </div>
        </>
      )}
    </div>
  )
}

const TRAY_LABELS: Record<TraySize, string> = {
  half_tray:      'Half Tray',
  medium_tray:    'Medium Tray',
  full_tray:      'Full Tray',
  per_piece:      'Per Piece',
  per_lb:         'Per Lb (Loose)',
  boxed_per_lb:   'Per Lb (Boxed)',
  custom:         'Custom',
}

const EVENT_TYPES = ['Wedding','Birthday','Corporate','Graduation','Religious','Baby Shower','Anniversary','Other']
const ALL = '__all__'

// Order panel tabs — grouped so each screen is scannable at a glance instead
// of one long scroll: build the order, review who/when it's for, check
// supply readiness, then audit what changed.
type OrderTab = 'menu' | 'details' | 'ingredients' | 'changelog'
const ORDER_TABS: { key: OrderTab; label: string }[] = [
  { key: 'menu',        label: 'Menu & Payment' },
  { key: 'details',     label: 'Details' },
  { key: 'ingredients', label: 'Ingredients' },
  { key: 'changelog',   label: 'Changelog' },
]

// ── Helpers ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, warn }: { label: string; value: string | number; warn?: boolean }) {
  return (
    <div className="card p-4">
      <p className="section-label">{label}</p>
      <p className={['text-2xl font-semibold mt-1', warn ? 'text-danger' : 'text-stone-900'].join(' ')}>{value}</p>
    </div>
  )
}

// ── Order row ────────────────────────────────────────────────────────────────
function OrderRow({ order, onClick }: { order: CateringOrder; onClick: () => void }) {
  const total   = order.quote_amount ?? 0
  const depPaid = order.deposit_paid_at ? (order.deposit_amount ?? 0) : 0
  const balPaid = order.balance_paid_at ? (order.balance_due ?? 0) : 0
  const outstanding = total - depPaid - balPaid

  return (
    <tr className="hover:bg-stone-50 cursor-pointer transition-colors" onClick={onClick}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {order.has_customer_update && (
            <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" title="Customer updated their selections" />
          )}
          <span className="font-mono text-xs text-stone-500">{order.order_number ?? '—'}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <p className="font-medium text-stone-900">{order.client_name}</p>
          {order.has_customer_update && (
            <span className="px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-700 text-2xs font-semibold">Updated</span>
          )}
        </div>
        {order.client_phone && <p className="text-xs text-stone-400">{order.client_phone}</p>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-sm text-stone-600">
          <CalendarDays size={13} className="text-stone-400" />
          {order.event_date}
        </div>
        {order.event_type && <p className="text-xs text-stone-400 mt-0.5">{order.event_type}</p>}
      </td>
      <td className="px-4 py-3 text-center text-sm font-medium text-stone-700">
        {order.guest_count ?? <span className="text-stone-300">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {total > 0
          ? <span className="font-medium text-stone-900">${total.toFixed(2)}</span>
          : <span className="text-stone-300 text-xs">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {outstanding > 0.005
          ? <span className="text-sm font-medium text-warning-dark">${outstanding.toFixed(2)}</span>
          : total > 0
            ? <span className="text-xs font-medium text-success-dark">Paid</span>
            : <span className="text-stone-300 text-xs">—</span>}
      </td>
      <td className="px-4 py-3">
        <Badge variant={STATUS_VARIANTS[order.status]}>{STATUS_LABELS[order.status]}</Badge>
      </td>
      <td className="px-3 py-3">
        <ChevronRight size={14} className="text-stone-400" />
      </td>
    </tr>
  )
}

// ── Searchable, category-grouped item picker ─────────────────────────────────
function ItemPicker({ onAdd }: { onAdd: (id: string) => void }) {
  const { data: allItems = [] }    = useMenuItems()
  const { data: categories = [] }  = useMenuCategories()
  const [search, setSearch]        = useState('')
  const [open, setOpen]            = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const cateringItems = allItems.filter(i => i.available_catering)

  const q = search.trim().toLowerCase()
  const filtered = q
    ? cateringItems.filter(i => i.name.toLowerCase().includes(q) || (i.regional_name ?? '').toLowerCase().includes(q))
    : cateringItems

  const grouped = groupMenuItemsByCategory(filtered, categories)

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        placeholder="Search &amp; add item…"
        value={search}
        onFocus={() => setOpen(true)}
        onChange={e => { setSearch(e.target.value); setOpen(true) }}
        className="w-full border border-dashed border-brand-400 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400 bg-brand-50/40 placeholder:text-stone-400"
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-xl max-h-72 overflow-y-auto">
          {grouped.length === 0 ? (
            <p className="px-3 py-3 text-sm text-stone-400 text-center">No items found</p>
          ) : grouped.map(({ cat, items }) => (
            <div key={cat.id}>
              <div className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-stone-400 bg-stone-50 border-b border-stone-100 sticky top-0">
                {cat.name}
              </div>
              {items.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={e => {
                    e.preventDefault()
                    onAdd(item.id)
                    setSearch('')
                    setOpen(false)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-brand-50 hover:text-brand-800 transition-colors flex items-center justify-between group"
                >
                  <span>{item.name}</span>
                  {item.regional_name && (
                    <span className="text-xs text-stone-400 group-hover:text-brand-500 ml-2 truncate max-w-[120px]">
                      {item.regional_name}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Print card ───────────────────────────────────────────────────────────────
function printOrderCard(order: CateringOrder, allMenuItems: MenuItem[] = [], includeIngredients = false) {
  const BRAND = '#c94a1a'

  // Date formatting
  const eventDate = order.event_date ? new Date(order.event_date + 'T12:00:00') : null
  const fmtDate = eventDate
    ? eventDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    : '—'
  const fmtDay  = eventDate
    ? `(${eventDate.toLocaleDateString('en-US', { weekday: 'long' })})`
    : ''
  const fmtTime = order.event_time
    ? (() => {
        const [h, m] = order.event_time.split(':').map(Number)
        const ampm = h >= 12 ? 'PM' : 'AM'
        const h12  = h % 12 || 12
        return `${String(h12).padStart(2,'0')}:${String(m).padStart(2,'0')} ${ampm}`
      })()
    : '—'
  const maskedPhone = order.client_phone
    ? `•••• ${order.client_phone.replace(/\D/g,'').slice(-4)}`
    : '—'

  const orderNum = order.order_number ?? 'CAT-ORDER'

  // Status badge color
  const statusColors: Record<string, { bg: string; color: string }> = {
    inquiry:       { bg: '#f3f4f6', color: '#374151' },
    quoted:        { bg: '#dbeafe', color: '#1d4ed8' },
    confirmed:     { bg: '#dbeafe', color: '#1d4ed8' },
    deposit_paid:  { bg: '#fef9c3', color: '#854d0e' },
    in_production: { bg: '#fef9c3', color: '#854d0e' },
    delivered:     { bg: '#dcfce7', color: '#166534' },
    completed:     { bg: '#dcfce7', color: '#166534' },
    cancelled:     { bg: '#fee2e2', color: '#991b1b' },
  }
  const sc = statusColors[order.status] ?? { bg: '#f3f4f6', color: '#374151' }

  // Icon SVGs (inline, 18px)
  const iconUser     = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${BRAND}" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>`
  const iconCal      = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${BRAND}" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`
  const iconClock    = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${BRAND}" stroke-width="2"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/></svg>`
  const iconUsers    = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${BRAND}" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`
  const iconPin      = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${BRAND}" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`

  const icBox = (svg: string) =>
    `<div style="width:26px;height:26px;border-radius:50%;background:#fff4ee;display:flex;align-items:center;justify-content:center;margin-bottom:6px">${svg}</div>`

  const itemRows = (order.items ?? []).map(it => {
    const sizePart = it.tray_size && it.tray_size !== 'custom'
      ? ` (${TRAY_LABELS[it.tray_size] ?? it.tray_size})`
      : ''
    const serves = it.serves_per_unit ? it.quantity * it.serves_per_unit : null
    return `<tr>
      <td style="padding:7px 8px;border-bottom:1px solid #f0ece8;vertical-align:top">
        <span style="color:${BRAND};margin-right:8px;font-size:14px;line-height:1">•</span>${it.name}${sizePart}
      </td>
      <td style="padding:7px 8px;border-bottom:1px solid #f0ece8;text-align:center;font-weight:600">${it.quantity}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #f0ece8;text-align:center">${serves ?? '—'}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #f0ece8;color:#6b7280;font-style:italic">${it.notes ?? '—'}</td>
    </tr>`
  }).join('')

  const notes = [order.special_requests, order.dietary_requirements].filter(Boolean).join(' · ') || '—'

  // Ingredients checklist
  const { procured, ingredients, noRecipe } = computeSupplies(order.items ?? [], allMenuItems)
  function fmtQtyPrint(n: number) { return n % 1 < 0.05 ? Math.round(n).toString() : n.toFixed(1) }

  const procuredRows = procured.map(item =>
    `<tr>
      <td style="padding:6px 10px;border-bottom:1px solid #f0ece8;vertical-align:middle">
        <span style="display:inline-block;width:14px;height:14px;border:1.5px solid #9ca3af;border-radius:3px;margin-right:8px;vertical-align:middle;flex-shrink:0"></span>
        ${item.name}
      </td>
      <td style="padding:6px 10px;border-bottom:1px solid #f0ece8;text-align:center;font-weight:600;color:#92400e">× ${fmtQtyPrint(item.qty)}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #f0ece8;color:#6b7280;font-size:11px">order from supplier</td>
    </tr>`
  ).join('')

  const ingredientRows = ingredients.map(item =>
    `<tr>
      <td style="padding:6px 10px;border-bottom:1px solid #f0ece8;vertical-align:middle">
        <span style="display:inline-block;width:14px;height:14px;border:1.5px solid #9ca3af;border-radius:3px;margin-right:8px;vertical-align:middle;flex-shrink:0"></span>
        ${item.name}
      </td>
      <td style="padding:6px 10px;border-bottom:1px solid #f0ece8;text-align:center;font-weight:600;color:#374151">${fmtQtyPrint(item.qty)} ${item.unit}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #f0ece8;color:#6b7280;font-size:11px">ingredient</td>
    </tr>`
  ).join('')

  const hasSupplies = procured.length > 0 || ingredients.length > 0

  // Only built when the caller asked for it, and always starts on its own
  // printed page rather than just avoiding a mid-block page split.
  const suppliesSection = (includeIngredients && hasSupplies) ? `
  <!-- Ingredients checklist — separate page -->
  <div style="margin-top:12px;border:1px solid #e5ddd4;border-radius:10px;overflow:hidden;page-break-inside:avoid;break-inside:avoid;page-break-before:always;break-before:page">
    <div style="padding:10px 16px;background:#fff8f3;border-bottom:2px solid #c94a1a;display:flex;align-items:center;gap:10px">
      <img src="${window.location.origin}/logo-amruthahar.png" style="width:36px;height:auto;object-fit:contain" alt="">
      <div>
        <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:#c94a1a">INGREDIENTS & SUPPLIES CHECKLIST</div>
        <div style="font-size:10px;color:#9ca3af;margin-top:1px">${orderNum} · ${order.client_name}</div>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:13px;background:#fff">
      <thead>
        <tr style="border-bottom:1px solid #e5ddd4;background:#fafafa">
          <th style="padding:6px 10px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af">ITEM</th>
          <th style="padding:6px 10px;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;width:80px">QTY</th>
          <th style="padding:6px 10px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;width:120px">TYPE</th>
        </tr>
      </thead>
      <tbody>
        ${procured.length > 0 ? `
        <tr style="background:#fffbeb"><td colspan="3" style="padding:5px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#92400e">Order from Suppliers</td></tr>
        ${procuredRows}` : ''}
        ${ingredients.length > 0 ? `
        <tr style="background:#f9fafb"><td colspan="3" style="padding:5px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280">Ingredients to Stock</td></tr>
        ${ingredientRows}` : ''}
        ${noRecipe.length > 0 ? `
        <tr><td colspan="3" style="padding:8px 10px;font-size:11px;color:#9ca3af;font-style:italic">No recipe on file: ${noRecipe.join(', ')}</td></tr>` : ''}
      </tbody>
    </table>
  </div>` : ''

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Catering Order ${orderNum}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:13px;color:#1c1917;background:#f9f6f2;padding:20px}
  .card{background:#fff;border:1px solid #e5ddd4;border-radius:12px;overflow:hidden;max-width:700px;margin:0 auto}
  @page{size:A4 portrait;margin:12mm}
  @media print{
    html,body{padding:0;margin:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .card{border:1px solid #e5ddd4 !important;border-radius:8px !important;max-width:100%;page-break-inside:avoid;break-inside:avoid}
  }
</style>
</head>
<body>
<div class="card">

  <!-- Header -->
  <div style="padding:14px 20px;border-bottom:2px solid ${BRAND};display:flex;align-items:flex-start;justify-content:space-between;gap:16px">
    <div style="display:flex;align-items:center;gap:16px">
      <!-- Logo -->
      <img src="${window.location.origin}/logo-amruthahar.png" style="width:64px;height:auto;object-fit:contain;flex-shrink:0" alt="Amruth Ahar">
      <!-- Order info -->
      <div>
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:#9ca3af;margin-bottom:4px">CATERING ORDER</div>
        <div style="font-size:26px;font-weight:800;letter-spacing:-0.02em;color:#1c1917">${orderNum}</div>
        <div style="margin-top:6px">
          <span style="background:${sc.bg};color:${sc.color};font-size:11px;font-weight:600;padding:3px 10px;border-radius:99px">${STATUS_LABELS[order.status]}</span>
        </div>
      </div>
    </div>
    <!-- Date -->
    <div style="text-align:right;flex-shrink:0">
      <div style="display:flex;align-items:center;gap:6px;justify-content:flex-end;margin-bottom:4px">
        ${iconCal}
      </div>
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.1em;color:#9ca3af;margin-bottom:3px">ORDER DATE</div>
      <div style="font-size:14px;font-weight:600;color:#1c1917">${new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
    </div>
  </div>

  <!-- Info strip: 4 columns -->
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr;border-bottom:1px solid #f0ece8">
    <div style="padding:10px 12px;border-right:1px solid #f0ece8">
      ${icBox(iconUser)}
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#9ca3af;margin-bottom:4px">CUSTOMER</div>
      <div style="font-size:13px;font-weight:700;color:#1c1917">${order.client_name}</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px">${maskedPhone}</div>
    </div>
    <div style="padding:10px 12px;border-right:1px solid #f0ece8">
      ${icBox(iconCal)}
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#9ca3af;margin-bottom:4px">EVENT DATE</div>
      <div style="font-size:13px;font-weight:700;color:#1c1917">${fmtDate}</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px">${fmtDay}</div>
    </div>
    <div style="padding:10px 12px;border-right:1px solid #f0ece8">
      ${icBox(iconClock)}
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#9ca3af;margin-bottom:4px">EVENT TIME</div>
      <div style="font-size:13px;font-weight:700;color:#1c1917">${fmtTime}</div>
    </div>
    <div style="padding:10px 12px">
      ${icBox(iconUsers)}
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#9ca3af;margin-bottom:4px">GUEST COUNT</div>
      <div style="font-size:18px;font-weight:800;color:#1c1917">${order.guest_count ?? '—'}</div>
    </div>
  </div>

  ${order.venue_address ? `
  <!-- Venue -->
  <div style="background:#fff8f3;padding:12px 20px;border-bottom:1px solid #f0ece8;display:flex;align-items:center;gap:10px">
    ${iconPin}
    <span style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:${BRAND};margin-right:8px">VENUE</span>
    <span style="font-size:13px;color:#374151">${order.venue_address}</span>
  </div>` : ''}

  <!-- Items -->
  <div style="padding:14px 20px">
    <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${BRAND};border-bottom:1px solid #f0ece8;padding-bottom:8px;margin-bottom:0">ORDER ITEMS</div>
    <table style="width:100%;border-collapse:collapse;font-size:13px">
      <thead>
        <tr style="border-bottom:1px solid #e5ddd4">
          <th style="padding:8px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;padding-left:26px">ITEM</th>
          <th style="padding:8px;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;width:60px">QTY</th>
          <th style="padding:8px;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;width:70px">SERVES</th>
          <th style="padding:8px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;width:140px">NOTES</th>
        </tr>
      </thead>
      <tbody>${itemRows}</tbody>
    </table>
  </div>

  <!-- Notes + Status checklist -->
  <div style="display:grid;grid-template-columns:1fr 1fr;border-top:1px solid #f0ece8">
    <div style="padding:12px 16px;border-right:1px solid #f0ece8">
      <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${BRAND};margin-bottom:6px">NOTES</div>
      <div style="font-size:12px;color:#374151;line-height:1.4">${notes}</div>
    </div>
    <div style="padding:12px 16px">
      <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${BRAND};margin-bottom:8px">STATUS</div>
      <div style="display:flex;gap:20px;align-items:center">
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:#374151;cursor:pointer">
          <input type="checkbox" style="width:16px;height:16px;accent-color:${BRAND}"> In Prep
        </label>
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:#374151;cursor:pointer">
          <input type="checkbox" style="width:16px;height:16px;accent-color:${BRAND}"> Packed
        </label>
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:#374151;cursor:pointer">
          <input type="checkbox" style="width:16px;height:16px;accent-color:${BRAND}"> Delivered
        </label>
      </div>
    </div>
  </div>

</div>

${suppliesSection}
</body>
</html>`

  const win = window.open('', '_blank', 'width=760,height=960')
  if (!win) return
  win.document.write(html)
  win.document.close()
  win.onload = () => win.print()
}

// ── Catering Supplies ────────────────────────────────────────────────────────

interface SupplyLine {
  key: string
  name: string
  qty: number
  unit: string
}

function parseServesCount(s: string): number {
  const m = s.match(/\d+(\.\d+)?/)
  return m ? parseFloat(m[0]) : 1
}

function computeSupplies(
  orderItems: Array<{ menu_item_id: string | null; quantity: number; serves_per_unit: number | null; name: string }>,
  allMenuItems: MenuItem[]
): { procured: SupplyLine[]; ingredients: SupplyLine[]; noRecipe: string[] } {
  const miMap = new Map(allMenuItems.map(m => [m.id, m]))
  const procured: SupplyLine[] = []
  const ingMap = new Map<string, SupplyLine>()
  const noRecipe: string[] = []

  for (const item of orderItems) {
    if (!item.menu_item_id) { noRecipe.push(item.name); continue }
    const mi = miMap.get(item.menu_item_id)
    if (!mi) continue

    if (mi.source_type === 'procured') {
      procured.push({ key: `proc_${mi.id}`, name: mi.name, qty: item.quantity, unit: 'unit' })
      continue
    }

    if (!mi.ai_recipe?.ingredients?.length) { noRecipe.push(mi.name); continue }

    const recipeServes = parseServesCount(mi.ai_recipe.serves)
    const orderServes  = (item.quantity || 1) * (item.serves_per_unit || 1)
    const scale        = recipeServes > 0 ? orderServes / recipeServes : 1

    for (const ing of mi.ai_recipe.ingredients) {
      const rawQty = parseFloat(ing.qty) || 0
      const key    = `${ing.name.trim().toLowerCase()}_${ing.unit.trim().toLowerCase()}`
      const prev   = ingMap.get(key)
      if (prev) {
        ingMap.set(key, { ...prev, qty: prev.qty + rawQty * scale })
      } else {
        ingMap.set(key, { key, name: ing.name.trim(), qty: rawQty * scale, unit: ing.unit.trim() })
      }
    }
  }

  return {
    procured,
    ingredients: Array.from(ingMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    noRecipe,
  }
}

function SuppliesSection({
  items,
  allMenuItems,
  orderId,
}: {
  items: Array<{ menu_item_id: string | null; quantity: number; serves_per_unit: number | null; name: string }>
  allMenuItems: MenuItem[]
  orderId?: string
}) {
  const storageKey = `catering_supplies_${orderId ?? 'new'}`
  const [open, setOpen] = useState(false)
  const [checked, setChecked] = useState<Set<string>>(() => {
    try {
      const s = localStorage.getItem(storageKey)
      return s ? new Set(JSON.parse(s) as string[]) : new Set()
    } catch { return new Set() }
  })

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify([...checked])) } catch { /* ignore */ }
  }, [checked, storageKey])

  const { procured, ingredients, noRecipe } = computeSupplies(items, allMenuItems)
  const allKeys   = [...procured, ...ingredients].map(i => i.key)
  const doneCount = allKeys.filter(k => checked.has(k)).length
  const total     = allKeys.length

  function toggle(key: string) {
    setChecked(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
  }

  function fmtQty(n: number) { return n % 1 < 0.05 ? Math.round(n).toString() : n.toFixed(1) }

  if (items.length === 0) return null

  return (
    <section className="border border-brand-100 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-brand-50 hover:bg-brand-100/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ShoppingCart size={14} className="text-brand-600" />
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-700">Supplies Needed</span>
          {total > 0 && (
            <span className={cn(
              'text-xs font-medium px-2 py-0.5 rounded-full',
              doneCount === total ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-600'
            )}>
              {doneCount}/{total} confirmed
            </span>
          )}
        </div>
        <ChevronDown size={14} className={cn('text-brand-500 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="p-4 space-y-4 bg-white">

          {/* Items to order from suppliers */}
          {procured.length > 0 && (
            <div>
              <p className="text-2xs font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                Order from Suppliers
              </p>
              <div className="space-y-1.5">
                {procured.map(item => (
                  <label key={item.key} className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors',
                    checked.has(item.key)
                      ? 'border-green-200 bg-green-50'
                      : 'border-amber-100 bg-amber-50/60 hover:border-amber-200'
                  )}>
                    <input
                      type="checkbox"
                      checked={checked.has(item.key)}
                      onChange={() => toggle(item.key)}
                      className="shrink-0 accent-green-600"
                    />
                    <span className={cn(
                      'flex-1 text-sm font-medium transition-colors',
                      checked.has(item.key) ? 'line-through text-stone-400' : 'text-stone-800'
                    )}>
                      {item.name}
                    </span>
                    <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full shrink-0">
                      × {fmtQty(item.qty)}
                    </span>
                    {checked.has(item.key) && (
                      <span className="text-xs text-green-600 font-medium shrink-0">Ordered ✓</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Calculated ingredients */}
          {ingredients.length > 0 && (
            <div>
              <p className="text-2xs font-semibold uppercase tracking-wider text-stone-400 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-stone-300 inline-block" />
                Ingredients to Stock
              </p>
              <div className="divide-y divide-stone-50 rounded-lg border border-stone-100 overflow-hidden">
                {ingredients.map(item => (
                  <label key={item.key} className={cn(
                    'flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-stone-50 transition-colors',
                    checked.has(item.key) ? 'bg-green-50/40' : 'bg-white'
                  )}>
                    <input
                      type="checkbox"
                      checked={checked.has(item.key)}
                      onChange={() => toggle(item.key)}
                      className="shrink-0 accent-brand-600"
                    />
                    <span className={cn(
                      'flex-1 text-sm transition-colors',
                      checked.has(item.key) ? 'line-through text-stone-400' : 'text-stone-700'
                    )}>
                      {item.name}
                    </span>
                    <span className={cn(
                      'text-xs font-semibold px-2 py-0.5 rounded shrink-0 transition-colors',
                      checked.has(item.key) ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
                    )}>
                      {fmtQty(item.qty)} {item.unit}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Items missing recipes */}
          {noRecipe.length > 0 && (
            <div className="bg-stone-50 rounded-lg px-3 py-2.5 text-xs text-stone-400 space-y-1">
              <p className="font-medium text-stone-500">No recipe on file for:</p>
              {noRecipe.map((name, i) => <p key={i}>• {name}</p>)}
              <p className="mt-1 italic">Add a recipe in Products → item detail to include these.</p>
            </div>
          )}

          {total === 0 && noRecipe.length === 0 && (
            <p className="text-sm text-stone-400 text-center py-3">
              Add menu items with recipes to see supply requirements.
            </p>
          )}

          {total > 0 && doneCount === total && (
            <div className="text-center text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
              All supplies confirmed — ready to cook ✓
            </div>
          )}
        </div>
      )}
    </section>
  )
}

// ── Changelog / revision history ─────────────────────────────────────────────
// Simple staff-facing viewer over the append-only snapshot trail so staff can
// spot what a customer changed (or what a teammate changed) without having to
// reconstruct it from memory.
function RevisionHistorySection({ cateringId }: { cateringId: string }) {
  const { data: revisions = [], isLoading } = useCateringOrderRevisions(cateringId)
  const [open, setOpen] = useState(true)

  if (isLoading) return null
  if (revisions.length === 0) {
    return (
      <p className="text-sm text-stone-400 text-center py-8">
        No changes yet — this fills in once the order's been edited or the customer updates their selections.
      </p>
    )
  }

  return (
    <section className="border border-stone-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-stone-50 hover:bg-stone-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History size={12} className="text-stone-400" />
          <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Changelog</span>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-stone-200 text-stone-600">
            {revisions.length}
          </span>
        </div>
        <ChevronDown size={14} className={cn('text-stone-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="p-3 space-y-2 max-h-64 overflow-y-auto bg-white">
          {revisions.map(rev => (
            <div key={rev.id} className="flex items-start gap-2.5 border border-stone-200 rounded-lg px-3 py-2 text-xs">
              <div className={cn(
                'shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center',
                rev.changed_by === 'customer' ? 'bg-brand-100 text-brand-700' : 'bg-stone-100 text-stone-500',
              )}>
                {rev.changed_by === 'customer' ? <UserRound size={11} /> : <User size={11} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-stone-800">
                    {rev.changed_by === 'customer' ? 'Customer updated' : (rev.changed_by_name ?? 'Staff') + ' updated'}
                  </p>
                  <p className="text-2xs text-stone-400 shrink-0">
                    {new Date(rev.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
                <p className="text-stone-500 mt-0.5">
                  Previous version — {rev.items.length} item{rev.items.length === 1 ? '' : 's'}
                  {rev.quote_amount != null && `, $${rev.quote_amount.toFixed(2)}`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Quote preview ─────────────────────────────────────────────────────────────
// Read-only, staff-facing mirror of exactly what the customer's page shows
// once this order is confirmed — same line items grouped the same way (by
// Section, not category — that's what the customer actually sees), same
// "Comp"/discount/delivery/tax breakdown — computed live from whatever's
// currently in the form, including unsaved edits, so staff can sanity-check
// before hitting Save. Printable and Excel-exportable for sending to a
// customer who wants something outside this app.
interface QuotePreviewLine {
  name: string
  tray_size: TraySize | null
  quantity: number
  unit_price: number
  is_complimentary: boolean
  catering_section: CateringSectionKey | null
}
function QuotePreviewModal({
  orderNumber, clientName, eventDate, eventTime, guestCount, fulfillmentType, eventSections,
  items, itemsSubtotal, discountAmt, discountPct, deliveryFeeAmt, deliveryIsComplimentary, taxAmt, totalWithTax, onClose,
}: {
  orderNumber: string | null
  clientName: string
  eventDate: string
  eventTime: string
  guestCount: string
  fulfillmentType: FulfillmentType | ''
  eventSections: CateringSectionKey[]
  items: QuotePreviewLine[]
  itemsSubtotal: number
  discountAmt: number
  discountPct: number | null
  deliveryFeeAmt: number
  deliveryIsComplimentary: boolean
  taxAmt: number
  totalWithTax: number
  onClose: () => void
}) {
  // Same grouping helper the customer's own page uses (usePublicCateringQuote),
  // keyed on catering_section — so this preview can't silently drift from
  // what actually renders on their screen. Bucket list is the fixed, standard
  // occasions selected for this event, in canonical order.
  const sectionBuckets = CATERING_SECTIONS
    .filter(k => eventSections.includes(k))
    .map((k, i) => ({ id: k, name: CATERING_SECTION_LABELS[k], display_order: i }))
  const grouped = groupByCategory(
    items.map(it => ({ ...it, categoryId: it.catering_section })),
    sectionBuckets,
  )

  const complimentaryValue = items.reduce((s, i) => s + (i.is_complimentary ? i.quantity * i.unit_price : 0), 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-semibold text-stone-900 flex items-center gap-1.5">
              <Eye size={14} className="text-stone-400" />
              Quote Preview
            </h3>
            <p className="text-2xs text-stone-400">What your customer sees</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => exportQuotePreviewExcel({
                orderNumber, clientName, eventDate, eventTime, guestCount, fulfillmentType,
                grouped, itemsSubtotal, discountAmt, discountPct, deliveryFeeAmt, deliveryIsComplimentary,
                complimentaryValue, taxAmt, totalWithTax,
              })}
              className="p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700"
              title="Export to Excel"
            >
              <FileSpreadsheet size={15} />
            </button>
            <button
              onClick={() => printQuotePreview({
                orderNumber, clientName, eventDate, eventTime, guestCount, fulfillmentType,
                grouped, itemsSubtotal, discountAmt, discountPct, deliveryFeeAmt, deliveryIsComplimentary,
                complimentaryValue, taxAmt, totalWithTax,
              })}
              className="p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700"
              title="Print"
            >
              <Printer size={15} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-stone-100 text-stone-400">
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-xs bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5">
            <div><span className="text-stone-400">Client</span><p className="font-medium text-stone-800">{clientName || '—'}</p></div>
            <div><span className="text-stone-400">Event</span><p className="font-medium text-stone-800">{eventDate || '—'}{eventTime ? ` · ${eventTime}` : ''}</p></div>
            <div><span className="text-stone-400">Guests</span><p className="font-medium text-stone-800">{guestCount || '—'}</p></div>
            <div>
              <span className="text-stone-400">Delivery or Pickup</span>
              <p className="font-medium text-stone-800">
                {fulfillmentType === 'delivery' ? 'Delivery' : fulfillmentType === 'pickup' ? 'Pickup' : 'To be confirmed'}
              </p>
            </div>
          </div>

          {items.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-6">No items on this order yet.</p>
          ) : (
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-stone-500 text-xs">Item</th>
                    <th className="px-3 py-2 text-center font-medium text-stone-500 text-xs">Qty</th>
                    <th className="px-3 py-2 text-right font-medium text-stone-500 text-xs">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {grouped.map(section => (
                    <Fragment key={section.id}>
                      <tr className="bg-stone-50">
                        <td colSpan={3} className="px-3 py-1 text-2xs font-bold uppercase tracking-widest text-stone-400 border-y border-stone-200">
                          {section.name}
                        </td>
                      </tr>
                      {section.rows.map((it, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <p className="font-medium text-stone-800">{it.name}</p>
                            <p className="text-2xs text-stone-400">
                              {it.tray_size && it.tray_size !== 'custom' ? TRAY_LABELS[it.tray_size] : null}
                            </p>
                          </td>
                          <td className="px-3 py-2 text-center text-stone-700">×{it.quantity}</td>
                          <td className="px-3 py-2 text-right">
                            {it.is_complimentary ? (
                              <span className="text-success-dark font-medium text-xs">Comp</span>
                            ) : (
                              <span className="text-stone-700">${(it.quantity * it.unit_price).toFixed(2)}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {items.length > 0 && (
            <div className="pt-2 border-t border-stone-100 space-y-1 text-sm">
              {/* Same line order everywhere: Menu & Payment card, this preview,
                  print, and Excel export — Items subtotal → Comp → Discount →
                  Delivery → Subtotal → tax → Total with tax. */}
              <div className="flex justify-between text-stone-500">
                <span>Items subtotal</span><span>${itemsSubtotal.toFixed(2)}</span>
              </div>
              {complimentaryValue > 0 && (
                <div className="flex justify-between text-success-dark">
                  <span>Complimentary items</span><span>−${complimentaryValue.toFixed(2)}</span>
                </div>
              )}
              {discountAmt > 0 && (
                <div className="flex justify-between text-stone-500">
                  <span>Discount{discountPct ? ` (${discountPct.toFixed(0)}%)` : ''}</span>
                  <span>−${discountAmt.toFixed(2)}</span>
                </div>
              )}
              {deliveryFeeAmt > 0 && (
                <div className="flex justify-between text-stone-500">
                  <span>Delivery fee</span><span>${deliveryFeeAmt.toFixed(2)}</span>
                </div>
              )}
              {deliveryFeeAmt > 0 && deliveryIsComplimentary && (
                <div className="flex justify-between text-success-dark">
                  <span>Delivery waived (comp)</span><span>−${deliveryFeeAmt.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-stone-900 pt-1 border-t border-stone-100">
                <span>Subtotal</span><span>${(totalWithTax - taxAmt).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-stone-400 text-xs">
                <span>+ 7% tax</span><span>${taxAmt.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-stone-900 pt-1 border-t border-stone-100">
                <span>Total with tax</span><span>${totalWithTax.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Shared shape both the print window and the Excel export build from — one
// place to keep them from drifting apart from each other or the on-screen preview.
interface QuotePreviewExportData {
  orderNumber: string | null
  clientName: string
  eventDate: string
  eventTime: string
  guestCount: string
  fulfillmentType: FulfillmentType | ''
  grouped: { id: string; name: string; rows: QuotePreviewLine[] }[]
  itemsSubtotal: number
  discountAmt: number
  discountPct: number | null
  deliveryFeeAmt: number
  deliveryIsComplimentary: boolean
  complimentaryValue: number
  taxAmt: number
  totalWithTax: number
}

function printQuotePreview(data: QuotePreviewExportData) {
  const BRAND = '#c94a1a'
  const fulfillmentLabel = data.fulfillmentType === 'delivery' ? 'Delivery' : data.fulfillmentType === 'pickup' ? 'Pickup' : 'To be confirmed'

  const sectionRows = data.grouped.map(section => `
    <tr style="background:#faf8f6"><td colspan="3" style="padding:6px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;border-top:1px solid #f0ece8;border-bottom:1px solid #f0ece8">${section.name}</td></tr>
    ${section.rows.map(it => `
      <tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f0ece8">
          ${it.name}${it.tray_size && it.tray_size !== 'custom' ? ` <span style="color:#9ca3af;font-size:11px">(${TRAY_LABELS[it.tray_size] ?? it.tray_size})</span>` : ''}
        </td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0ece8;text-align:center">×${it.quantity}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0ece8;text-align:right">
          ${it.is_complimentary ? '<span style="color:#166534;font-weight:600">Comp</span>' : `$${(it.quantity * it.unit_price).toFixed(2)}`}
        </td>
      </tr>`).join('')}
  `).join('')

  // Same line order everywhere: Menu & Payment card, Quote Preview, this
  // print output, and Excel export — Items subtotal → Comp → Discount →
  // Delivery → Subtotal → tax → Total with tax.
  const preTax = data.totalWithTax - data.taxAmt
  const summaryRows = [
    `<tr><td colspan="2" style="padding:4px 10px;color:#6b7280">Items subtotal</td><td style="padding:4px 10px;text-align:right;color:#6b7280">$${data.itemsSubtotal.toFixed(2)}</td></tr>`,
    data.complimentaryValue > 0 ? `<tr><td colspan="2" style="padding:4px 10px;color:#166534">Complimentary items</td><td style="padding:4px 10px;text-align:right;color:#166534">−$${data.complimentaryValue.toFixed(2)}</td></tr>` : '',
    data.discountAmt > 0 ? `<tr><td colspan="2" style="padding:4px 10px;color:#6b7280">Discount${data.discountPct ? ` (${data.discountPct.toFixed(0)}%)` : ''}</td><td style="padding:4px 10px;text-align:right;color:#6b7280">−$${data.discountAmt.toFixed(2)}</td></tr>` : '',
    data.deliveryFeeAmt > 0 ? `<tr><td colspan="2" style="padding:4px 10px;color:#6b7280">Delivery fee</td><td style="padding:4px 10px;text-align:right;color:#6b7280">$${data.deliveryFeeAmt.toFixed(2)}</td></tr>` : '',
    data.deliveryFeeAmt > 0 && data.deliveryIsComplimentary ? `<tr><td colspan="2" style="padding:4px 10px;color:#166534">Delivery waived (comp)</td><td style="padding:4px 10px;text-align:right;color:#166534">−$${data.deliveryFeeAmt.toFixed(2)}</td></tr>` : '',
    `<tr><td colspan="2" style="padding:6px 10px;font-weight:700;border-top:1px solid #e5ddd4">Subtotal</td><td style="padding:6px 10px;text-align:right;font-weight:700;border-top:1px solid #e5ddd4">$${preTax.toFixed(2)}</td></tr>`,
    `<tr><td colspan="2" style="padding:4px 10px;color:#6b7280;font-size:12px">+ 7% tax</td><td style="padding:4px 10px;text-align:right;color:#6b7280;font-size:12px">$${data.taxAmt.toFixed(2)}</td></tr>`,
    `<tr><td colspan="2" style="padding:8px 10px;font-weight:700;font-size:15px;border-top:1px solid #e5ddd4">Total with tax</td><td style="padding:8px 10px;text-align:right;font-weight:700;font-size:15px;border-top:1px solid #e5ddd4">$${data.totalWithTax.toFixed(2)}</td></tr>`,
  ].join('')

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Quote ${data.orderNumber ?? ''}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:13px;color:#1c1917;background:#f9f6f2;padding:20px}
  .card{background:#fff;border:1px solid #e5ddd4;border-radius:12px;overflow:hidden;max-width:640px;margin:0 auto}
  table{width:100%;border-collapse:collapse}
  @page{size:A4 portrait;margin:12mm}
  @media print{
    html,body{padding:0;margin:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .card{border:1px solid #e5ddd4 !important;max-width:100%}
  }
</style>
</head>
<body>
<div class="card">
  <div style="padding:16px 20px;border-bottom:2px solid ${BRAND}">
    <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:#9ca3af;margin-bottom:4px">CATERING QUOTE</div>
    <div style="font-size:22px;font-weight:800;letter-spacing:-0.02em">${data.orderNumber ?? 'Draft'}</div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border-bottom:1px solid #f0ece8;padding:12px 20px;font-size:12px">
    <div style="padding:4px 0"><span style="color:#9ca3af">Client</span><br><strong>${data.clientName || '—'}</strong></div>
    <div style="padding:4px 0"><span style="color:#9ca3af">Event</span><br><strong>${data.eventDate || '—'}${data.eventTime ? ` · ${data.eventTime}` : ''}</strong></div>
    <div style="padding:4px 0"><span style="color:#9ca3af">Guests</span><br><strong>${data.guestCount || '—'}</strong></div>
    <div style="padding:4px 0"><span style="color:#9ca3af">Delivery or Pickup</span><br><strong>${fulfillmentLabel}</strong></div>
  </div>
  <table>
    <thead>
      <tr style="border-bottom:1px solid #e5ddd4">
        <th style="padding:6px 10px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af">Item</th>
        <th style="padding:6px 10px;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af">Qty</th>
        <th style="padding:6px 10px;text-align:right;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af">Price</th>
      </tr>
    </thead>
    <tbody>${sectionRows}</tbody>
  </table>
  <table>
    <tbody>${summaryRows}</tbody>
  </table>
</div>
</body>
</html>`

  const win = window.open('', '_blank', 'width=760,height=960')
  if (!win) return
  win.document.write(html)
  win.document.close()
  win.onload = () => win.print()
}

function exportQuotePreviewExcel(data: QuotePreviewExportData) {
  const fulfillmentLabel = data.fulfillmentType === 'delivery' ? 'Delivery' : data.fulfillmentType === 'pickup' ? 'Pickup' : 'To be confirmed'

  const rows: Record<string, string | number>[] = []
  for (const section of data.grouped) {
    for (const it of section.rows) {
      rows.push({
        Section:  section.name,
        Item:     it.name,
        Size:     it.tray_size && it.tray_size !== 'custom' ? (TRAY_LABELS[it.tray_size] ?? it.tray_size) : '',
        Qty:      it.quantity,
        Price:    it.is_complimentary ? 'Comp' : it.unit_price.toFixed(2),
        Total:    it.is_complimentary ? 0 : Number((it.quantity * it.unit_price).toFixed(2)),
      })
    }
  }

  // Same line order everywhere: Menu & Payment card, Quote Preview, print,
  // and this export — Items subtotal → Comp → Discount → Delivery →
  // Subtotal → tax → Total with tax.
  const summaryRows: Record<string, string | number>[] = [
    { Section: '', Item: 'Items subtotal', Size: '', Qty: '', Price: '', Total: data.itemsSubtotal },
    ...(data.complimentaryValue > 0 ? [{ Section: '', Item: 'Complimentary items', Size: '', Qty: '', Price: '', Total: -data.complimentaryValue }] : []),
    ...(data.discountAmt > 0 ? [{ Section: '', Item: `Discount${data.discountPct ? ` (${data.discountPct.toFixed(0)}%)` : ''}`, Size: '', Qty: '', Price: '', Total: -data.discountAmt }] : []),
    ...(data.deliveryFeeAmt > 0 ? [{ Section: '', Item: 'Delivery fee', Size: '', Qty: '', Price: '', Total: data.deliveryFeeAmt }] : []),
    ...(data.deliveryFeeAmt > 0 && data.deliveryIsComplimentary ? [{ Section: '', Item: 'Delivery waived (comp)', Size: '', Qty: '', Price: '', Total: -data.deliveryFeeAmt }] : []),
    { Section: '', Item: 'Subtotal', Size: '', Qty: '', Price: '', Total: data.totalWithTax - data.taxAmt },
    { Section: '', Item: '+ 7% tax', Size: '', Qty: '', Price: '', Total: data.taxAmt },
    { Section: '', Item: 'Total with tax', Size: '', Qty: '', Price: '', Total: data.totalWithTax },
  ]

  const wb = XLSX.utils.book_new()

  const infoRows = [
    { Field: 'Order #', Value: data.orderNumber ?? 'Draft' },
    { Field: 'Client', Value: data.clientName },
    { Field: 'Event date', Value: data.eventDate },
    { Field: 'Event time', Value: data.eventTime },
    { Field: 'Guests', Value: data.guestCount },
    { Field: 'Delivery or Pickup', Value: fulfillmentLabel },
  ]
  const wsInfo = XLSX.utils.json_to_sheet(infoRows, { skipHeader: true })
  XLSX.utils.book_append_sheet(wb, wsInfo, 'Order Info')

  const wsItems = XLSX.utils.json_to_sheet([...rows, {}, ...summaryRows] as Record<string, unknown>[])
  XLSX.utils.book_append_sheet(wb, wsItems, 'Quote')

  XLSX.writeFile(wb, `catering-quote-${data.orderNumber ?? 'draft'}.xlsx`)
}

// ── Meal-occasion sections (Breakfast/Lunch/Evening/Dinner) ─────────────────
// A fixed, standard set — not a freeform per-restaurant label bank. Toggled
// per event (Details tab); whichever are on become the buckets shown in
// Menu Items to add/move dishes into. Nothing here is shared across orders,
// so switching one event's sections off can never strip a tag off another
// order's items the way a shared, deletable row used to.
function EventSectionsPicker({
  selected, onChange,
}: {
  selected: CateringSectionKey[]
  onChange: (next: CateringSectionKey[]) => void
}) {
  function toggle(key: CateringSectionKey) {
    onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key])
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {CATERING_SECTIONS.map(key => (
        <button
          key={key}
          type="button"
          onClick={() => toggle(key)}
          className={cn(
            'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
            selected.includes(key)
              ? 'bg-brand-600 border-brand-600 text-white'
              : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400',
          )}
        >
          {CATERING_SECTION_LABELS[key]}
        </button>
      ))}
    </div>
  )
}

// A section header row doubles as a drop target — dragging an item row onto
// it moves that item into this section (at the top), even if the section is
// currently empty and has no rows of its own to drop onto.
function DraggableSectionHeader({ sectionKey, colSpan, children }: { sectionKey: string; colSpan: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `header:${sectionKey}` })
  return (
    <tr ref={setNodeRef} className={cn('bg-stone-50', isOver && 'bg-brand-100/60 ring-1 ring-inset ring-brand-300')}>
      <td colSpan={colSpan} className="px-3 py-1 text-2xs font-bold uppercase tracking-widest text-stone-400 border-y border-stone-200">
        {children}
      </td>
    </tr>
  )
}

// The drag handle is a separate small element inside the row, not the whole
// `<tr>` — the row is full of inputs/selects that need normal clicks and
// focus to keep working.
function DraggableItemRow({ idx, children }: { idx: number; children: (handle: React.ReactNode) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `item:${idx}` })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }
  const handle = (
    <button type="button" {...attributes} {...listeners} className="text-stone-300 hover:text-stone-500 cursor-grab active:cursor-grabbing touch-none">
      <GripVertical size={13} />
    </button>
  )
  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-stone-50">
      {children(handle)}
    </tr>
  )
}

// ── Order slide panel ────────────────────────────────────────────────────────
function OrderPanel({
  order,
  onClose,
  onSaved,
}: {
  order: CateringOrder | null
  onClose: () => void
  onSaved: (order: CateringOrder) => void
}) {
  const { data: allItems = [] }    = useMenuItems()
  const { data: categories = [] }  = useMenuCategories()
  const { data: pricing }          = useCateringPricing()
  const { data: teamMembers = [] } = useTeamMembers()
  const currentUser = useAuthStore(s => s.user)
  const { mutateAsync: save, isPending: saving }             = useSaveCateringOrder()
  const { mutateAsync: updateStatus, isPending: advancing }  = useUpdateCateringStatus()
  const { mutateAsync: markSeen }                             = useMarkCateringUpdateSeen()
  // A small movement threshold before a drag "activates" — otherwise a plain
  // click on the handle (or a mis-aimed tap) would register as a drag.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  // New inquiries default the point of contact to whoever's creating it —
  // still editable, just saves the common case of re-picking yourself.
  const blank = useCallback(() => ({
    client_name: '', client_phone: '', client_email: '', client_company: '',
    event_date: '', event_time: '', event_type: '', guest_count: '',
    venue_address: '', venue_notes: '',
    event_sections: [] as CateringSectionKey[],
    fulfillment_type: '' as FulfillmentType | '',
    delivery_fee: '', delivery_is_complimentary: false,
    discount_percentage: '',
    status: 'inquiry' as CateringStatus,
    deposit_percentage: '25', deposit_paid_at: '', balance_paid_at: '',
    special_requests: '', dietary_requirements: '', notes: '', internal_notes: '',
    assigned_to: currentUser?.id ?? '',
  }), [currentUser])

  const [form, setForm]   = useState(blank())
  const [items, setItems] = useState<Omit<CateringOrderItem,'id'|'catering_id'>[]>([])
  const [error, setError] = useState<string | null>(null)
  const [matrixOpen, setMatrixOpen] = useState(false)
  const [depositMode, setDepositMode]     = useState<'pct' | 'amount'>('pct')
  const [depositAmtInput, setDepositAmtInput] = useState('')
  const [discountMode, setDiscountMode]         = useState<'pct' | 'amount'>('amount')
  const [discountAmtInput, setDiscountAmtInput] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [printMenuOpen, setPrintMenuOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<OrderTab>('menu')

  // RezzAI: turn pasted notes/conversation text into a structured inquiry
  const [parsing, setParsing]       = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [parseSummary, setParseSummary] = useState<string | null>(null)

  // Set right before onSaved() fires from our own "Save" (stay-open) click —
  // the reset effect below checks it so a self-triggered refresh of `order`
  // doesn't clobber the local form/items state with the save response, which
  // doesn't include `items` at all (a separate table, not re-fetched here).
  const skipNextResetRef = useRef(false)

  function handleCopyCustomerLink() {
    if (!order?.order_number) return
    const url = `${window.location.origin}/q/${order.order_number}`
    navigator.clipboard.writeText(url)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  useEffect(() => {
    if (skipNextResetRef.current) {
      skipNextResetRef.current = false
      return
    }
    if (order) {
      setForm({
        client_name:          order.client_name,
        client_phone:         order.client_phone ?? '',
        client_email:         order.client_email ?? '',
        client_company:       order.client_company ?? '',
        event_date:           order.event_date,
        event_time:           order.event_time ?? '',
        event_type:           order.event_type ?? '',
        guest_count:          order.guest_count?.toString() ?? '',
        venue_address:        order.venue_address ?? '',
        venue_notes:          order.venue_notes ?? '',
        event_sections:            order.event_sections ?? [],
        fulfillment_type:          order.fulfillment_type ?? '',
        delivery_fee:              order.delivery_fee?.toString() ?? '',
        delivery_is_complimentary: order.delivery_is_complimentary ?? false,
        discount_percentage:  order.discount_percentage?.toString() ?? '',
        status:               order.status,
        deposit_percentage:   order.deposit_percentage?.toString() ?? '25',
        deposit_paid_at:      order.deposit_paid_at ? order.deposit_paid_at.split('T')[0] : '',
        // note: deposit_mode and depositAmtInput are separate state, reset below
        balance_paid_at:      order.balance_paid_at ? order.balance_paid_at.split('T')[0] : '',
        special_requests:     order.special_requests ?? '',
        dietary_requirements: order.dietary_requirements ?? '',
        notes:                order.notes ?? '',
        internal_notes:       order.internal_notes ?? '',
        assigned_to:          order.assigned_to ?? '',
      })
      setItems((order.items ?? []).map(i => ({
        menu_item_id:         i.menu_item_id,
        name:                 i.name,
        tray_size:            i.tray_size,
        quantity:             i.quantity,
        serves_per_unit:      i.serves_per_unit,
        unit:                 i.unit,
        unit_price:           i.unit_price,
        is_complimentary:     i.is_complimentary ?? false,
        notes:                i.notes,
        sort_order:           i.sort_order,
        catering_section:     i.catering_section ?? null,
      })))
      setDepositMode('pct')
      setDepositAmtInput(order.deposit_amount?.toString() ?? '')
      setDiscountMode(order.discount_percentage ? 'pct' : 'amount')
      setDiscountAmtInput(order.discount_amount?.toString() ?? '')
      if (order.has_customer_update) void markSeen(order.id)
    } else {
      setForm(blank())
      setItems([])
      setDepositMode('pct')
      setDepositAmtInput('')
      setDiscountMode('amount')
      setDiscountAmtInput('')
    }
    setActiveTab('menu')
    setError(null)
  }, [order, markSeen, blank])

  function f(key: keyof typeof form, val: string) {
    setForm(p => ({ ...p, [key]: val }))
  }

  const TAX_RATE = 0.07
  const itemsSubtotal      = items.reduce((s, i) => s + (i.is_complimentary ? 0 : i.quantity * i.unit_price), 0)
  const complimentaryValue = items.reduce((s, i) => s + (i.is_complimentary ? i.quantity * i.unit_price : 0), 0)
  // Delivery fee only ever applies to a delivery order — pickup means
  // nothing extra, full stop, regardless of what's sitting in the field.
  // When it does apply, it always shows at full value (staff can see what
  // it would normally cost), but — same "Comp" pattern as items — is
  // excluded from the total when waived.
  const deliveryFeeAmt      = form.fulfillment_type === 'delivery' ? (parseFloat(form.delivery_fee) || 0) : 0
  const deliveryChargeApplied = form.delivery_is_complimentary ? 0 : deliveryFeeAmt
  const discountPct      = parseFloat(form.discount_percentage) || 0
  const discountAmtFixed = parseFloat(discountAmtInput) || 0
  // Discount is a % of items only — delivery fee is never discounted.
  const discountAmt      = discountMode === 'pct'
    ? (itemsSubtotal * discountPct) / 100
    : discountAmtFixed
  const calculatedTotal = Math.max(0, itemsSubtotal + deliveryChargeApplied - discountAmt)
  const taxAmt           = calculatedTotal * TAX_RATE
  const totalWithTax     = calculatedTotal + taxAmt
  const depositPct      = parseFloat(form.deposit_percentage) || 25
  const depositAmtFixed = parseFloat(depositAmtInput) || 0
  // Deposit/balance split the tax-INCLUSIVE total — what the customer
  // actually owes — not the pre-tax subtotal. Splitting the pre-tax number
  // was undercounting by exactly the tax amount (advance + balance would
  // never add up to what's actually due).
  const depositAmt      = depositMode === 'pct'
    ? (totalWithTax * depositPct) / 100
    : depositAmtFixed
  const balanceDue  = totalWithTax - depositAmt
  const totalServes = items.reduce((s, i) => s + i.quantity * (i.serves_per_unit ?? 0), 0)

  // Grouped by this order's own section tags (Breakfast/Lunch/Evening/
  // Dinner) — per line item, not per product, so the same dish can sit in
  // two sections at once as two separate rows. Each row keeps its real
  // index into `items` so edits/reorders land on the right one.
  const itemsWithIndex = items.map((it, idx) => ({ ...it, _idx: idx }))
  const sectionRank = (id: string) => { const i = CATERING_SECTIONS.indexOf(id as CateringSectionKey); return i === -1 ? Infinity : i }
  // Within a section, sub-cluster by the item's own product category
  // (Appetizers, Frys, Daals, Rice, Sweets…) purely for readability — this is
  // the existing menu_categories taxonomy, looked up per row via menu_item_id.
  // Free-text/RezzAI rows with no menu_item_id fall into "Other".
  const catById = new Map(categories.map(c => [c.id, c]))
  const catNameFor = (menuItemId: string | null) => {
    const mi = menuItemId ? allItems.find(a => a.id === menuItemId) : null
    return mi?.category_id ? (catById.get(mi.category_id)?.name ?? 'Other') : 'Other'
  }
  const catOrderFor = (menuItemId: string | null) => {
    const mi = menuItemId ? allItems.find(a => a.id === menuItemId) : null
    return mi?.category_id ? (catById.get(mi.category_id)?.display_order ?? 999) : 999
  }
  const groupedItems = (() => {
    const bySection = new Map<string, typeof itemsWithIndex>()
    // Seed every section chosen for this event, even with zero items yet —
    // so it shows up as an actual bucket to drag/move dishes into, not just
    // something you can pick from a dropdown after the fact.
    for (const key of form.event_sections) bySection.set(key, [])
    for (const it of itemsWithIndex) {
      const key = it.catering_section ?? '__ungrouped__'
      bySection.set(key, [...(bySection.get(key) ?? []), it])
    }
    return [...bySection.entries()]
      .sort(([a], [b]) => sectionRank(a) - sectionRank(b))
      .map(([id, rows]) => ({
        id,
        name: id === '__ungrouped__' ? 'Ungrouped' : (CATERING_SECTION_LABELS[id as CateringSectionKey] ?? 'Ungrouped'),
        // Category is the primary sort within a section (so same-category
        // dishes cluster visually), sort_order breaks ties within a category.
        rows: [...rows].sort((a, b) =>
          catOrderFor(a.menu_item_id) - catOrderFor(b.menu_item_id)
          || a.sort_order - b.sort_order),
      }))
  })()

  // Drag a row onto another row to reorder within its section, or onto a
  // different section's header to move it there — one gesture does both
  // reordering and re-sectioning, rather than a dropdown + separate up/down
  // buttons per row. Only the target group's sort_order gets renumbered
  // (sequentially, from scratch — robust regardless of starting values).
  function handleItemDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (!activeId.startsWith('item:') || activeId === overId) return
    const fromIdx = parseInt(activeId.slice(5), 10)
    if (!items[fromIdx]) return

    let targetSectionKey: string
    let targetRows: typeof itemsWithIndex
    let insertPos: number

    if (overId.startsWith('header:')) {
      targetSectionKey = overId.slice(7)
      targetRows = groupedItems.find(g => g.id === targetSectionKey)?.rows ?? []
      insertPos = 0
    } else {
      const overIdx = parseInt(overId.slice(5), 10)
      const group = groupedItems.find(g => g.rows.some(r => r._idx === overIdx))
      if (!group) return
      targetSectionKey = group.id
      targetRows = group.rows
      insertPos = targetRows.findIndex(r => r._idx === overIdx)
    }

    const newSection = targetSectionKey === '__ungrouped__' ? null : (targetSectionKey as CateringSectionKey)
    const withoutDragged = targetRows.filter(r => r._idx !== fromIdx)
    const finalPos = Math.min(insertPos, withoutDragged.length)
    withoutDragged.splice(finalPos, 0, { ...items[fromIdx], _idx: fromIdx })

    setItems(prev => {
      const next = [...prev]
      withoutDragged.forEach((r, i) => {
        next[r._idx] = { ...next[r._idx], catering_section: newSection, sort_order: i }
      })
      return next
    })
  }

  // 'min' is only meaningful for the per-lb sizes (minimum order quantity,
  // in lbs) — null for trays/pieces, which have no enforced minimum.
  function pricingForSize(menuItemId: string | null, size: TraySize | null) {
    if (!menuItemId || !size || !pricing) return { price: 0, serves: null, min: null }
    const p = pricing.get(menuItemId)
    if (!p) return { price: 0, serves: null, min: null }
    switch (size) {
      case 'half_tray':    return { price: p.half_tray_price    ?? 0, serves: p.half_tray_serves ?? null, min: null }
      case 'medium_tray':  return { price: p.medium_tray_price  ?? 0, serves: p.medium_tray_serves ?? null, min: null }
      case 'full_tray':    return { price: p.full_tray_price    ?? 0, serves: p.full_tray_serves ?? null, min: null }
      case 'per_piece':    return { price: p.per_piece_price    ?? 0, serves: null, min: null }
      case 'per_lb':       return { price: p.per_lb_price       ?? 0, serves: null, min: p.per_lb_min ?? null }
      case 'boxed_per_lb': return { price: p.boxed_per_lb_price ?? 0, serves: null, min: p.boxed_per_lb_min ?? null }
      default:             return { price: 0, serves: null, min: null }
    }
  }

  function addItem(menuItemId: string) {
    const mi = allItems.find(i => i.id === menuItemId)
    if (!mi) return
    const p = pricing?.get(menuItemId)
    const defaultSize: TraySize = p?.half_tray_price != null ? 'half_tray'
      : p?.per_piece_price != null ? 'per_piece'
      : p?.per_lb_price != null ? 'per_lb'
      : p?.boxed_per_lb_price != null ? 'boxed_per_lb'
      : 'custom'
    const def = pricingForSize(menuItemId, defaultSize)
    setItems(prev => [...prev, {
      menu_item_id:    mi.id,
      name:            mi.name,
      tray_size:       defaultSize,
      quantity:        def.min ?? 1,
      serves_per_unit: def.serves,
      unit:            'unit',
      unit_price:      def.price,
      is_complimentary: false,
      notes:           null,
      sort_order:      prev.length,
      catering_section: null,
    }])
  }

  function updateItem(idx: number, patch: Partial<typeof items[0]>) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it))
  }

  // Adds directly from the menu-matrix picker — size and price are already known,
  // so no default-size guessing is needed the way addItem() does it.
  function addItemFromMatrix(sel: MatrixAddPayload) {
    setItems(prev => [...prev, {
      menu_item_id:    sel.menuItemId,
      name:            sel.name,
      tray_size:       sel.traySize,
      quantity:        sel.min ?? 1,
      serves_per_unit: sel.serves,
      unit:            'unit',
      unit_price:      sel.price,
      is_complimentary: false,
      notes:           null,
      sort_order:      prev.length,
      catering_section: null,
    }])
  }

  // Matrix cell tapped while already on the order — remove that item/size row.
  function removeItemFromMatrix(menuItemId: string, traySize: TraySize) {
    setItems(prev => {
      const idx = prev.findIndex(i => i.menu_item_id === menuItemId && i.tray_size === traySize)
      if (idx === -1) return prev
      return prev.filter((_, i) => i !== idx)
    })
  }

  // RezzAI: run free-form notes (handwritten, texted, emailed — any conversation)
  // through AI and pre-fill the inquiry form + item list from it. Never overwrites
  // fields the staff has already typed — it only fills blanks and appends items.
  async function handleParseText(pasteText: string) {
    if (!pasteText.trim()) return
    setParsing(true)
    setParseError(null)
    setParseSummary(null)
    try {
      const catMap = Object.fromEntries(categories.map(c => [c.id, c.name]))
      const cateringMenu = allItems
        .filter(i => i.available_catering)
        .map(i => ({ id: i.id, name: i.name, category: catMap[i.category_id ?? ''] ?? '' }))

      const { data, error: fnError } = await supabase.functions.invoke('parse-catering-inquiry', {
        body: {
          raw_text: pasteText,
          menu: cateringMenu,
          today: new Date().toISOString().slice(0, 10),
        },
      })
      if (fnError) throw new Error(fnError.message)

      const parsed = data as {
        client_name: string | null
        client_phone: string | null
        client_email: string | null
        client_company: string | null
        event_date: string | null
        event_time: string | null
        event_type: string | null
        guest_count: number | null
        venue_address: string | null
        special_requests: string | null
        dietary_requirements: string | null
        items: { menu_item_id: string | null; name: string; tray_size: TraySize | null; quantity: number }[]
        notes: string | null
      }

      setForm(f => ({
        ...f,
        client_name:          f.client_name          || parsed.client_name          || '',
        client_phone:         f.client_phone         || parsed.client_phone         || '',
        client_email:         f.client_email         || parsed.client_email         || '',
        client_company:       f.client_company       || parsed.client_company       || '',
        event_date:           f.event_date           || parsed.event_date           || '',
        event_time:           f.event_time           || parsed.event_time           || '',
        event_type:           f.event_type           || (parsed.event_type && EVENT_TYPES.includes(parsed.event_type) ? parsed.event_type : ''),
        guest_count:          f.guest_count          || (parsed.guest_count != null ? String(parsed.guest_count) : ''),
        venue_address:        f.venue_address        || parsed.venue_address        || '',
        special_requests:     f.special_requests     || parsed.special_requests     || '',
        dietary_requirements: f.dietary_requirements || parsed.dietary_requirements || '',
        internal_notes:       [f.internal_notes, parsed.notes].filter(Boolean).join(' · '),
      }))

      // Matched items get real pricing from the catering price list; anything
      // the AI couldn't confidently match is still added, flagged for review.
      const additions: typeof items = []
      let matchedCount = 0
      let unmatchedCount = 0
      for (const pi of parsed.items ?? []) {
        const mi = pi.menu_item_id ? allItems.find(a => a.id === pi.menu_item_id) : null
        if (mi) {
          matchedCount++
          const p = pricing?.get(mi.id)
          const size: TraySize = pi.tray_size ?? (p?.half_tray_price != null ? 'half_tray'
            : p?.per_piece_price != null ? 'per_piece' : 'custom')
          const priced = pricingForSize(mi.id, size)
          additions.push({
            menu_item_id: mi.id, name: mi.name, tray_size: size, quantity: pi.quantity || 1,
            serves_per_unit: priced.serves, unit: 'unit', unit_price: priced.price,
            is_complimentary: false, catering_section: null,
            notes: null, sort_order: 0,
          })
        } else {
          unmatchedCount++
          additions.push({
            menu_item_id: null, name: pi.name, tray_size: pi.tray_size, quantity: pi.quantity || 1,
            serves_per_unit: null, unit: 'unit', unit_price: 0,
            is_complimentary: false, catering_section: null,
            notes: 'Needs review — not matched to a menu item', sort_order: 0,
          })
        }
      }
      setItems(prev => [...prev, ...additions.map((a, i) => ({ ...a, sort_order: prev.length + i }))])

      setParseSummary(
        'Filled in from your note'
        + (matchedCount   ? ` · ${matchedCount} item${matchedCount === 1 ? '' : 's'} added`     : '')
        + (unmatchedCount ? ` · ${unmatchedCount} need${unmatchedCount === 1 ? 's' : ''} review` : '')
      )
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Could not read that note. Check that ANTHROPIC_API_KEY is set in Supabase Edge Function secrets.')
    } finally {
      setParsing(false)
    }
  }

  async function handleSave(closeAfter: boolean) {
    if (!form.client_name || !form.event_date) {
      setError('Client name and event date are required.')
      setActiveTab('details')
      return
    }
    if (!form.client_phone.trim()) {
      setError('Phone number is required — it doubles as the passcode customers use to view their order online.')
      setActiveTab('details')
      return
    }
    setError(null)
    try {
      const saved = await save({
        order: {
          id:                   order?.id,
          client_name:          form.client_name,
          client_phone:         form.client_phone || null,
          client_email:         form.client_email || null,
          client_company:       form.client_company || null,
          event_date:           form.event_date,
          event_time:           form.event_time || null,
          event_type:           form.event_type || null,
          guest_count:          form.guest_count ? parseInt(form.guest_count) : null,
          venue_address:        form.venue_address || null,
          venue_notes:          form.venue_notes || null,
          event_sections:            form.event_sections,
          fulfillment_type:          form.fulfillment_type || null,
          delivery_fee:              form.delivery_fee ? parseFloat(form.delivery_fee) : null,
          delivery_is_complimentary: form.delivery_is_complimentary,
          discount_percentage:  discountMode === 'pct'
            ? discountPct
            : itemsSubtotal > 0 ? (discountAmtFixed / itemsSubtotal) * 100 : null,
          discount_amount:      discountAmt || null,
          status:               form.status,
          // Tax-inclusive — what the customer actually owes, and what
          // deposit/balance below are split from. Not the pre-tax subtotal.
          quote_amount:         totalWithTax || null,
          deposit_percentage:   depositMode === 'pct'
            ? depositPct
            : totalWithTax > 0 ? (depositAmtFixed / totalWithTax) * 100 : null,
          deposit_amount:       depositAmt || null,
          deposit_paid_at:      form.deposit_paid_at || null,
          balance_due:          balanceDue || null,
          balance_paid_at:      form.balance_paid_at || null,
          special_requests:     form.special_requests || null,
          dietary_requirements: form.dietary_requirements || null,
          notes:                form.notes || null,
          internal_notes:       form.internal_notes || null,
          assigned_to:          form.assigned_to || null,
        },
        items,
      })
      // The mutation only returns the `catering_orders` row (no `items` join),
      // so feeding it back into `order` would make the reset effect below wipe
      // our just-saved local items/form state. Skip that one reset.
      skipNextResetRef.current = true
      onSaved(saved)
      if (closeAfter) onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  type AdvanceConfirm = {
    nextStatus:  CateringStatus
    nextLabel:   string
    showDeposit: boolean
    showBalance: boolean
    markDeposit: boolean
    markBalance: boolean
  }
  const [advanceConfirm, setAdvanceConfirm] = useState<AdvanceConfirm | null>(null)

  function handleAdvanceClick() {
    if (!order) return
    const next = STATUS_FLOW[order.status]
    if (!next) return

    const total      = order.quote_amount ?? 0
    const depPaid    = !!order.deposit_paid_at
    const balPaid    = !!order.balance_paid_at
    const showDeposit = total > 0 && ['in_production','delivered','completed'].includes(next.next)
    const showBalance = total > 0 && ['delivered','completed'].includes(next.next)

    if (showDeposit || showBalance) {
      setAdvanceConfirm({
        nextStatus:  next.next,
        nextLabel:   next.label,
        showDeposit,
        showBalance,
        markDeposit: depPaid,   // pre-tick if already paid
        markBalance: balPaid,
      })
    } else {
      void doAdvance(next.next)
    }
  }

  async function doAdvance(nextStatus: CateringStatus, markDeposit = false, markBalance = false) {
    if (!order) return
    const extra: Partial<Pick<typeof order, 'deposit_paid_at' | 'balance_paid_at'>> = {}
    if (markDeposit && !order.deposit_paid_at) extra.deposit_paid_at = new Date().toISOString()
    if (markBalance && !order.balance_paid_at) extra.balance_paid_at = new Date().toISOString()
    await updateStatus({ id: order.id, status: nextStatus, extra })
    setAdvanceConfirm(null)
    onClose()
  }

  async function handleCancel() {
    if (!order) return
    await updateStatus({ id: order.id, status: 'cancelled' })
    onClose()
  }

  const nextStep = order ? STATUS_FLOW[order.status] : null

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 sm:p-6" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col rounded-2xl overflow-hidden max-h-[92vh]" onClick={e => e.stopPropagation()}>

        {/* Header row */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold text-stone-900">
              {order ? (order.order_number ?? 'Order') : 'New Inquiry'}
            </h2>
            {order && (
              <Badge variant={STATUS_VARIANTS[order.status]}>
                {STATUS_LABELS[order.status]}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {order && order.order_number && (order.status === 'inquiry' || order.status === 'quoted') && (
              <button
                onClick={handleCopyCustomerLink}
                className="p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                title="Copy customer menu-selection link"
              >
                {linkCopied ? <Check size={15} className="text-success" /> : <Link2 size={15} />}
              </button>
            )}
            <button
              onClick={() => setPreviewOpen(true)}
              className="p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
              title="Preview quote — what the customer sees"
            >
              <Eye size={15} />
            </button>
            {order && (
              <div className="relative">
                <button
                  onClick={() => setPrintMenuOpen(v => !v)}
                  className="p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                  title="Print"
                >
                  <Printer size={15} />
                </button>
                {printMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setPrintMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-stone-200 rounded-lg shadow-lg py-1 w-56">
                      <button
                        onClick={() => { printOrderCard(order, allItems, false); setPrintMenuOpen(false) }}
                        className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 transition-colors"
                      >
                        Order card only
                      </button>
                      <button
                        onClick={() => { printOrderCard(order, allItems, true); setPrintMenuOpen(false) }}
                        className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 transition-colors"
                      >
                        Order card + ingredients
                        <span className="block text-2xs text-stone-400">Ingredients print on their own page</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {nextStep && order && order.status !== 'cancelled' && (
              <Button size="sm" onClick={handleAdvanceClick} loading={advancing}>
                <CheckCircle2 size={13} />
                {nextStep.label}
              </Button>
            )}
            {order && !['completed','cancelled'].includes(order.status) && (
              <button
                onClick={handleCancel}
                className="text-xs text-danger hover:underline px-2"
              >
                Cancel
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-stone-100 text-stone-500">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Lifecycle stepper */}
        {order && (
          <div className="px-6 py-3 border-b border-stone-200 bg-stone-50/60 shrink-0 overflow-x-auto">
            <LifecycleStepper status={order.status} />
          </div>
        )}

        {/* Payment summary */}
        {order && (
          <PaymentSummary
            total={totalWithTax}
            subtotalBeforeTax={calculatedTotal}
            depositAmount={depositAmt}
            depositPaidAt={form.deposit_paid_at}
            balanceAmount={balanceDue}
            balancePaidAt={form.balance_paid_at}
          />
        )}

        {/* Tabs */}
        <div className="border-b border-stone-200 shrink-0 overflow-x-auto">
          <div className="flex gap-0 px-6">
            {ORDER_TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab.key
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {error && <p className="text-sm text-danger bg-red-50 px-3 py-2 rounded">{error}</p>}

            {activeTab === 'menu' && (
            <>
            {/* RezzAI — build the inquiry from a pasted note or conversation */}
            <RezzAiPastePanel
              cta="Ask RezzAI — paste your notes to build this inquiry…"
              title="Paste your notes — RezzAI pulls out client, event & item details"
              placeholder="e.g. Priya Reddy 555-201-4477, 50 guests for a birthday on Aug 20 evening. Medium tray pongal, small tray aloo kurma, 30 idlies. Deliver to 12 Oak St."
              busy={parsing}
              error={parseError}
              summary={parseSummary}
              onRun={handleParseText}
              onDismissError={() => setParseError(null)}
            />
            </>
            )}

            {activeTab === 'details' && (
            <>
            {/* Client */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-3">Client</h3>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Name *" value={form.client_name} onChange={e => f('client_name', e.target.value)} />
                <div>
                  <Input label="Phone *" value={form.client_phone} onChange={e => f('client_phone', e.target.value)} />
                  <p className="text-2xs text-stone-400 mt-1">Full number — this is the customer's passcode to view/edit their order online.</p>
                </div>
                <Input label="Email" type="email" value={form.client_email} onChange={e => f('client_email', e.target.value)} />
                <Input label="Company / Organization" value={form.client_company} onChange={e => f('client_company', e.target.value)} />
              </div>
            </section>

            {/* Catering contact — who the customer sees as their POC on the public page */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-3">Catering Contact</h3>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-stone-700">Point of Contact</label>
                <select
                  value={form.assigned_to}
                  onChange={e => f('assigned_to', e.target.value)}
                  className="border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.filter(m => m.is_active).map(m => (
                    <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>
                  ))}
                </select>
                {(() => {
                  const contact = teamMembers.find(m => m.id === form.assigned_to)
                  if (!contact) {
                    return <p className="text-2xs text-stone-400">Defaults to you — pick someone else if this order belongs to a different team member. Shown to the customer on their order page.</p>
                  }
                  return (
                    <p className="text-2xs text-stone-500">
                      Customer will see: <strong className="text-stone-700">{contact.full_name}</strong>
                      {contact.phone ? ` · ${contact.phone}` : (
                        <span className="text-warning-dark"> · no phone on file — add one in Team settings so it shows up</span>
                      )}
                    </p>
                  )
                })()}
              </div>
            </section>

            {/* Event */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-3">Event</h3>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Event Date *" type="date" value={form.event_date} onChange={e => f('event_date', e.target.value)} />
                <Input label="Event Time" type="time" value={form.event_time} onChange={e => f('event_time', e.target.value)} />
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-stone-700">Event Type</label>
                  <select
                    value={form.event_type}
                    onChange={e => f('event_type', e.target.value)}
                    className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                  >
                    <option value="">Select…</option>
                    {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <Input label="Guest Count" type="number" value={form.guest_count} onChange={e => f('guest_count', e.target.value)} />
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-stone-700">Delivery or Pickup</label>
                  <select
                    value={form.fulfillment_type}
                    onChange={e => f('fulfillment_type', e.target.value)}
                    className="border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
                  >
                    <option value="">Not set</option>
                    <option value="delivery">Delivery</option>
                    <option value="pickup">Pickup</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 space-y-3">
                <Input label="Venue Address" value={form.venue_address} onChange={e => f('venue_address', e.target.value)} />
                <Input label="Venue Notes" value={form.venue_notes} onChange={e => f('venue_notes', e.target.value)} />
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                <label className="text-sm font-medium text-stone-700">Meal Occasions</label>
                <p className="text-xs text-stone-400">
                  Pick which apply to this event — they'll show up as sections in Menu &amp; Payment to add and move dishes into.
                </p>
                <EventSectionsPicker
                  selected={form.event_sections}
                  onChange={next => setForm(p => ({ ...p, event_sections: next }))}
                />
              </div>
            </section>
            </>
            )}

            {activeTab === 'menu' && (
            <>
            {/* Menu items */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-400">Menu Items</h3>
                {totalServes > 0 && (
                  <span className="text-xs text-stone-500 flex items-center gap-1">
                    <Users size={12} /> Serves ~{totalServes} people
                  </span>
                )}
              </div>

              {items.length > 0 && (
                <>
                  {form.event_sections.length === 0 && (
                    <p className="text-2xs text-stone-400 mb-1.5">
                      No meal occasions selected for this event yet — pick Breakfast/Lunch/Evening/Dinner in the
                      <button type="button" onClick={() => setActiveTab('details')} className="text-brand-600 font-medium hover:underline mx-1">
                        Details tab
                      </button>
                      to group items below.
                    </p>
                  )}
                  <p className="text-2xs text-stone-400 mb-1.5 flex items-center gap-1">
                    <GripVertical size={11} className="text-stone-300" />
                    Drag the handle to reorder, or drop onto another section to move an item there.
                  </p>
                  <div className="border border-stone-200 rounded overflow-hidden mb-3">
                    <table className="w-full text-xs">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="px-1 py-2" />
                          <th className="px-3 py-2 text-left font-medium text-stone-500">Item</th>
                          <th className="px-3 py-2 text-left font-medium text-stone-500">Size</th>
                          <th className="px-3 py-2 text-center font-medium text-stone-500">Qty</th>
                          <th className="px-3 py-2 text-center font-medium text-stone-500">Serves</th>
                          <th className="px-3 py-2 text-right font-medium text-stone-500">Price</th>
                          <th className="px-3 py-2 text-center font-medium text-stone-500">Comp</th>
                          <th className="px-3 py-2 text-right font-medium text-stone-500">Total</th>
                          <th className="px-2 py-2" />
                        </tr>
                      </thead>
                      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
                        <tbody className="divide-y divide-stone-100">
                          {groupedItems.map(group => (
                            <SortableContext
                              key={group.id}
                              items={group.rows.map(r => `item:${r._idx}`)}
                              strategy={verticalListSortingStrategy}
                            >
                              <DraggableSectionHeader sectionKey={group.id} colSpan={9}>
                                {group.name}
                              </DraggableSectionHeader>
                              {group.rows.map((item, i) => {
                                const idx = item._idx
                                const catName = catNameFor(item.menu_item_id)
                                const showCatHeader = i === 0 || catName !== catNameFor(group.rows[i - 1].menu_item_id)
                                return (
                                  <Fragment key={idx}>
                                    {showCatHeader && (
                                      <tr className="bg-stone-50/60">
                                        <td colSpan={9} className="pl-6 pr-3 py-1 text-2xs font-semibold uppercase tracking-wide text-stone-400">
                                          {catName}
                                        </td>
                                      </tr>
                                    )}
                                    <DraggableItemRow idx={idx}>
                                    {handle => (
                                      <>
                                        <td className="px-1 py-2">{handle}</td>
                                        <td className="px-3 py-2 font-medium text-stone-800">
                                          {item.name}
                                          {!item.catering_section && form.event_sections.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-1 mt-0.5">
                                              {form.event_sections.map(key => (
                                                <button
                                                  key={key}
                                                  type="button"
                                                  onClick={() => {
                                                    const targetLen = groupedItems.find(g => g.id === key)?.rows.length ?? 0
                                                    updateItem(idx, { catering_section: key, sort_order: targetLen })
                                                  }}
                                                  className="px-1.5 py-0.5 rounded-full border border-brand-200 bg-brand-50 text-2xs font-medium text-brand-700 hover:bg-brand-100 hover:border-brand-300"
                                                >
                                                  → {CATERING_SECTION_LABELS[key]}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-3 py-2">
                                          <select
                                            value={item.tray_size ?? 'custom'}
                                            onChange={e => {
                                              const size = e.target.value as TraySize
                                              const p = pricingForSize(item.menu_item_id, size)
                                              updateItem(idx, {
                                                tray_size: size, unit_price: p.price, serves_per_unit: p.serves,
                                                // Floor quantity to this size's minimum order (per-lb only) —
                                                // never lowers an already-larger quantity.
                                                quantity: p.min != null ? Math.max(item.quantity, p.min) : item.quantity,
                                              })
                                            }}
                                            className="border border-stone-300 rounded px-1.5 py-1 bg-white text-xs"
                                          >
                                            {Object.entries(TRAY_LABELS).map(([v, l]) => (
                                              <option key={v} value={v}>{l}</option>
                                            ))}
                                          </select>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <input
                                            type="text" inputMode="decimal"
                                            value={item.quantity}
                                            onChange={e => updateItem(idx, { quantity: parseFloat(e.target.value) || 0 })}
                                            className="w-12 text-center border border-stone-300 rounded px-1.5 py-1"
                                          />
                                          {(() => {
                                            const min = pricingForSize(item.menu_item_id, item.tray_size).min
                                            return min != null && item.quantity < min
                                              ? <p className="text-2xs text-warning-dark mt-0.5">min {min} lb</p>
                                              : null
                                          })()}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <input
                                            type="text" inputMode="decimal"
                                            value={item.serves_per_unit ?? ''}
                                            placeholder="—"
                                            onChange={e => updateItem(idx, { serves_per_unit: parseInt(e.target.value) || null })}
                                            className="w-12 text-center border border-stone-300 rounded px-1.5 py-1"
                                          />
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <input
                                            type="text" inputMode="decimal"
                                            value={item.unit_price}
                                            onChange={e => updateItem(idx, { unit_price: parseFloat(e.target.value) || 0 })}
                                            disabled={item.is_complimentary}
                                            className="w-20 text-right border border-stone-300 rounded px-1.5 py-1 disabled:bg-stone-50 disabled:text-stone-400"
                                          />
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <input
                                            type="checkbox"
                                            checked={item.is_complimentary}
                                            onChange={e => updateItem(idx, { is_complimentary: e.target.checked })}
                                            className="rounded border-stone-300"
                                            title="Mark as complimentary (comp) — excluded from the total"
                                          />
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          {item.is_complimentary ? (
                                            <p className="font-medium text-green-700">Comp</p>
                                          ) : (
                                            <p className="font-medium text-stone-900">${(item.quantity * item.unit_price).toFixed(2)}</p>
                                          )}
                                        </td>
                                        <td className="px-2 py-2">
                                          <button
                                            onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}
                                            className="text-stone-300 hover:text-danger transition-colors"
                                          >
                                            <Trash2 size={12} />
                                          </button>
                                        </td>
                                      </>
                                    )}
                                    </DraggableItemRow>
                                  </Fragment>
                                )
                              })}
                            </SortableContext>
                          ))}
                        </tbody>
                      </DndContext>
                    </table>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <ItemPicker onAdd={addItem} />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setMatrixOpen(true)}>
                  <ShoppingCart size={13} />
                  Browse Menu
                </Button>
              </div>

              <CateringMenuMatrixSheet
                open={matrixOpen}
                onClose={() => setMatrixOpen(false)}
                onAdd={addItemFromMatrix}
                onRemove={removeItemFromMatrix}
                orderItems={items}
              />

              {items.length > 0 && (
                <div className="mt-3 ml-auto w-full max-w-xs space-y-1 text-sm border border-stone-200 rounded-lg px-3 py-2.5 bg-stone-50">
                  <div className="flex justify-between text-stone-500">
                    <span>Items subtotal</span><span>${itemsSubtotal.toFixed(2)}</span>
                  </div>
                  {complimentaryValue > 0 && (
                    <div className="flex justify-between text-success-dark">
                      <span>Complimentary items</span><span>−${complimentaryValue.toFixed(2)}</span>
                    </div>
                  )}
                  {discountAmt > 0 && (
                    <div className="flex justify-between text-stone-500">
                      <span>Discount{discountMode === 'pct' && discountPct > 0 ? ` (${discountPct.toFixed(0)}%)` : ''}</span>
                      <span>−${discountAmt.toFixed(2)}</span>
                    </div>
                  )}
                  {deliveryFeeAmt > 0 && (
                    <div className="flex justify-between text-stone-500">
                      <span>Delivery fee</span>
                      <span>{form.delivery_is_complimentary ? '' : '+'}${deliveryFeeAmt.toFixed(2)}</span>
                    </div>
                  )}
                  {deliveryFeeAmt > 0 && form.delivery_is_complimentary && (
                    <div className="flex justify-between text-success-dark">
                      <span>Delivery waived (comp)</span><span>−${deliveryFeeAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-stone-900 pt-1 border-t border-stone-200">
                    <span>Subtotal</span><span>${calculatedTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-stone-400 text-xs">
                    <span>+ 7% tax</span><span>${taxAmt.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-stone-900 pt-1 border-t border-stone-200">
                    <span>Total with tax</span><span>${totalWithTax.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </section>
            </>
            )}

            {activeTab === 'ingredients' && (
              items.length > 0 ? (
                <SuppliesSection
                  items={items}
                  allMenuItems={allItems}
                  orderId={order?.id}
                />
              ) : (
                <p className="text-sm text-stone-400 text-center py-8">
                  Add menu items first — supply needs are calculated from what's on the order.
                </p>
              )
            )}

            {activeTab === 'menu' && (
            <>
            {/* Payment */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-3">Payment</h3>

              {form.fulfillment_type === 'delivery' && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  {form.venue_address
                    ? <>Delivering to: <strong>{form.venue_address}</strong> — confirm this is deliverable before setting the fee below.</>
                    : <>No venue address on file yet — add one above so you can confirm delivery is possible and price it accurately.</>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mb-3">
                {form.fulfillment_type === 'delivery' ? (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-stone-500">Delivery Fee</label>
                    <div className="flex items-center border border-stone-300 rounded overflow-hidden">
                      <span className="px-2 py-2 text-sm text-stone-500 bg-stone-50 border-r border-stone-300 select-none">$</span>
                      <input
                        type="text" inputMode="decimal"
                        value={form.delivery_fee}
                        onChange={e => f('delivery_fee', e.target.value)}
                        placeholder="0.00"
                        disabled={form.delivery_is_complimentary}
                        className="w-full px-2 py-2 text-sm focus:outline-none disabled:bg-stone-50 disabled:text-stone-400"
                      />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-stone-500 cursor-pointer mt-0.5">
                      <input
                        type="checkbox"
                        checked={form.delivery_is_complimentary}
                        onChange={e => setForm(p => ({ ...p, delivery_is_complimentary: e.target.checked }))}
                        className="rounded border-stone-300"
                      />
                      Comp — waive this delivery fee
                    </label>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-stone-500">Delivery Fee</label>
                    <p className="text-xs text-stone-400 px-3 py-2 border border-dashed border-stone-200 rounded">
                      {form.fulfillment_type === 'pickup' ? 'Pickup — no delivery fee' : 'Set Delivery/Pickup above first'}
                    </p>
                  </div>
                )}

                {/* Discount — % or $ toggle, same pattern as Advance/Deposit below */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-stone-500">Discount</label>
                    <div className="flex gap-0.5 bg-stone-100 p-0.5 rounded">
                      {(['pct', 'amount'] as const).map(mode => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setDiscountMode(mode)}
                          className={cn(
                            'px-2 py-0.5 rounded text-2xs font-medium transition-colors',
                            discountMode === mode
                              ? 'bg-white text-stone-800 shadow-sm'
                              : 'text-stone-500 hover:text-stone-700',
                          )}
                        >
                          {mode === 'pct' ? '% of items' : '$ fixed'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {discountMode === 'pct' ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-stone-300 rounded overflow-hidden">
                        <input
                          type="text" inputMode="decimal"
                          value={form.discount_percentage}
                          onChange={e => f('discount_percentage', e.target.value)}
                          className="w-14 px-2 py-2 text-sm text-center focus:outline-none"
                        />
                        <span className="px-2 text-sm text-stone-500 bg-stone-50 border-l border-stone-300 select-none">%</span>
                      </div>
                      <span className="text-sm font-semibold text-amber-700">= ${discountAmt.toFixed(2)}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-stone-300 rounded overflow-hidden">
                        <span className="px-2 py-2 text-sm text-stone-500 bg-stone-50 border-r border-stone-300 select-none">$</span>
                        <input
                          type="text" inputMode="decimal"
                          value={discountAmtInput}
                          onChange={e => setDiscountAmtInput(e.target.value)}
                          placeholder="0.00"
                          className="w-24 px-2 py-2 text-sm focus:outline-none"
                        />
                      </div>
                      {itemsSubtotal > 0 && discountAmtFixed > 0 && (
                        <span className="text-xs text-stone-400">
                          ({((discountAmtFixed / itemsSubtotal) * 100).toFixed(0)}% of items)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Total — full breakdown already shown once, in the summary
                    card above (Items subtotal → Comp → Discount → Delivery →
                    Total). This is just the number, for context next to the
                    Advance/Deposit box beside it. Tax-inclusive — this is
                    what Advance/Deposit below actually splits. */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-stone-500">Total (with tax)</label>
                  <div className="flex items-center gap-1 border border-stone-200 rounded px-3 py-2 bg-stone-50 text-sm font-semibold text-stone-900">
                    <DollarSign size={12} className="text-stone-400" />
                    {totalWithTax.toFixed(2)}
                  </div>
                  {totalWithTax > 0 && (
                    <p className="text-2xs text-stone-400">Subtotal before tax: ${calculatedTotal.toFixed(2)}</p>
                  )}
                </div>

                {/* Advance — % or $ toggle */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-stone-500">Advance / Deposit</label>
                    <div className="flex gap-0.5 bg-stone-100 p-0.5 rounded">
                      {(['pct', 'amount'] as const).map(mode => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setDepositMode(mode)}
                          className={cn(
                            'px-2 py-0.5 rounded text-2xs font-medium transition-colors',
                            depositMode === mode
                              ? 'bg-white text-stone-800 shadow-sm'
                              : 'text-stone-500 hover:text-stone-700',
                          )}
                        >
                          {mode === 'pct' ? '% of total' : '$ fixed'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {depositMode === 'pct' ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-stone-300 rounded overflow-hidden">
                        <input
                          type="text" inputMode="decimal"
                          value={form.deposit_percentage}
                          onChange={e => f('deposit_percentage', e.target.value)}
                          className="w-14 px-2 py-2 text-sm text-center focus:outline-none"
                        />
                        <span className="px-2 text-sm text-stone-500 bg-stone-50 border-l border-stone-300 select-none">%</span>
                      </div>
                      <span className="text-sm font-semibold text-amber-700">= ${depositAmt.toFixed(2)}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-stone-300 rounded overflow-hidden">
                        <span className="px-2 py-2 text-sm text-stone-500 bg-stone-50 border-r border-stone-300 select-none">$</span>
                        <input
                          type="text" inputMode="decimal"
                          value={depositAmtInput}
                          onChange={e => setDepositAmtInput(e.target.value)}
                          placeholder="0.00"
                          className="w-24 px-2 py-2 text-sm focus:outline-none"
                        />
                      </div>
                      {totalWithTax > 0 && depositAmtFixed > 0 && (
                        <span className="text-xs text-stone-400">
                          ({((depositAmtFixed / totalWithTax) * 100).toFixed(0)}% of total)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.deposit_paid_at}
                      onChange={e => f('deposit_paid_at', e.target.checked ? new Date().toISOString().split('T')[0] : '')}
                      className="rounded border-stone-300"
                    />
                    Advance / Deposit Paid
                  </label>
                  {form.deposit_paid_at && (
                    <input
                      type="date" value={form.deposit_paid_at}
                      onChange={e => f('deposit_paid_at', e.target.value)}
                      className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.balance_paid_at}
                      onChange={e => f('balance_paid_at', e.target.checked ? new Date().toISOString().split('T')[0] : '')}
                      className="rounded border-stone-300"
                    />
                    Full Payment Received
                  </label>
                  {form.balance_paid_at && (
                    <input
                      type="date" value={form.balance_paid_at}
                      onChange={e => f('balance_paid_at', e.target.value)}
                      className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  )}
                </div>
              </div>

              {totalWithTax > 0 && (
                <div className="mt-3 flex items-center justify-between text-sm border-t border-stone-100 pt-3">
                  <span className="text-stone-500">Balance remaining</span>
                  <span className="font-semibold text-stone-900">
                    ${(totalWithTax - (form.deposit_paid_at ? depositAmt : 0) - (form.balance_paid_at ? balanceDue : 0)).toFixed(2)}
                  </span>
                </div>
              )}
            </section>

            {/* Notes */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-3">Notes</h3>
              <div className="space-y-3">
                {[
                  { label: 'Special Requests', key: 'special_requests' as const },
                  { label: 'Dietary Requirements', key: 'dietary_requirements' as const },
                  { label: 'Internal Notes', key: 'internal_notes' as const },
                ].map(({ label, key }) => (
                  <div key={key} className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-stone-700">{label}</label>
                    <textarea
                      value={form[key]}
                      onChange={e => f(key, e.target.value)}
                      rows={2}
                      className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400 resize-none"
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Status override — subtle, for corrections */}
            {order && (
              <section>
                <h3 className="text-xs font-medium text-stone-400 mb-2">Change status</h3>
                <div className="flex flex-wrap gap-1.5">
                  {(['inquiry','quoted','confirmed','in_production','delivered','completed','cancelled'] as CateringStatus[]).map(s => (
                    <button
                      key={s}
                      type="button"
                      disabled={s === order.status || advancing}
                      onClick={() => updateStatus({ id: order.id, status: s }).then(() => onClose())}
                      className={[
                        'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
                        s === order.status
                          ? 'bg-stone-100 border-stone-300 text-stone-400 cursor-default'
                          : s === 'cancelled'
                            ? 'border-red-200 text-red-500 hover:bg-red-50'
                            : 'border-stone-200 text-stone-500 hover:bg-stone-100 hover:text-stone-800',
                      ].join(' ')}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </section>
            )}
            </>
            )}

            {/* Changelog — what changed and when, staff vs. customer */}
            {activeTab === 'changelog' && order && <RevisionHistorySection cateringId={order.id} />}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between shrink-0 bg-stone-50">
          <button onClick={onClose} className="text-sm text-stone-500 hover:text-stone-700">Discard</button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => handleSave(false)} loading={saving}>
              <Save size={14} />
              Save
            </Button>
            <Button onClick={() => handleSave(true)} loading={saving}>
              <Save size={14} />
              Save &amp; Close
            </Button>
          </div>
        </div>

      </div>

      {/* Payment check dialog — non-blocking, status always proceeds */}
      {advanceConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-semibold text-stone-900">
                Moving to <span className="text-brand-700">{STATUS_LABELS[advanceConfirm.nextStatus]}</span>
              </h3>
              <button onClick={() => setAdvanceConfirm(null)} className="p-1 rounded hover:bg-stone-100 text-stone-400">
                <X size={15} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-stone-500">Payment status for this order:</p>

              {advanceConfirm.showDeposit && (
                <label className={[
                  'flex items-start gap-3 p-3 rounded-lg border cursor-pointer',
                  advanceConfirm.markDeposit ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50',
                ].join(' ')}>
                  <input
                    type="checkbox"
                    checked={advanceConfirm.markDeposit}
                    onChange={e => setAdvanceConfirm(p => p ? { ...p, markDeposit: e.target.checked } : p)}
                    className="mt-0.5 rounded"
                  />
                  <div>
                    <p className={['text-sm font-medium', advanceConfirm.markDeposit ? 'text-green-800' : 'text-amber-800'].join(' ')}>
                      Advance / Deposit — {advanceConfirm.markDeposit ? 'paid ✓' : 'not yet collected'}
                    </p>
                    {!advanceConfirm.markDeposit && (
                      <p className="text-xs text-amber-600 mt-0.5">Tick to mark as received now</p>
                    )}
                  </div>
                </label>
              )}

              {advanceConfirm.showBalance && (
                <label className={[
                  'flex items-start gap-3 p-3 rounded-lg border cursor-pointer',
                  advanceConfirm.markBalance ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50',
                ].join(' ')}>
                  <input
                    type="checkbox"
                    checked={advanceConfirm.markBalance}
                    onChange={e => setAdvanceConfirm(p => p ? { ...p, markBalance: e.target.checked } : p)}
                    className="mt-0.5 rounded"
                  />
                  <div>
                    <p className={['text-sm font-medium', advanceConfirm.markBalance ? 'text-green-800' : 'text-orange-800'].join(' ')}>
                      Balance payment — {advanceConfirm.markBalance ? 'paid ✓' : 'outstanding'}
                    </p>
                    {!advanceConfirm.markBalance && (
                      <p className="text-xs text-orange-600 mt-0.5">Tick to mark as received now</p>
                    )}
                  </div>
                </label>
              )}
            </div>

            <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 flex justify-between items-center">
              <button onClick={() => setAdvanceConfirm(null)} className="text-sm text-stone-500 hover:text-stone-700">
                Go Back
              </button>
              <Button
                size="sm"
                onClick={() => doAdvance(advanceConfirm.nextStatus, advanceConfirm.markDeposit, advanceConfirm.markBalance)}
                loading={advancing}
              >
                <CheckCircle2 size={13} />
                {advanceConfirm.nextLabel}
              </Button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <QuotePreviewModal
          orderNumber={order?.order_number ?? null}
          clientName={form.client_name}
          eventDate={form.event_date}
          eventTime={form.event_time}
          guestCount={form.guest_count}
          fulfillmentType={form.fulfillment_type}
          eventSections={form.event_sections}
          items={items}
          itemsSubtotal={itemsSubtotal}
          discountAmt={discountAmt}
          discountPct={discountMode === 'pct' ? discountPct : null}
          deliveryFeeAmt={deliveryFeeAmt}
          deliveryIsComplimentary={form.delivery_is_complimentary}
          taxAmt={taxAmt}
          totalWithTax={totalWithTax}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────
export function CateringPage() {
  const { data: orders = [], isLoading } = useCateringOrders()
  const [statusFilter, setStatusFilter] = useState(ALL)
  const [panelOpen, setPanelOpen]   = useState(false)
  const [selected, setSelected]     = useState<CateringOrder | null>(null)

  if (isLoading) return <PageSpinner />

  const filtered = statusFilter === ALL ? orders : orders.filter(o => o.status === statusFilter)

  const completedRevenue = orders
    .filter(o => ['completed','delivered'].includes(o.status))
    .reduce((s, o) => s + (o.quote_amount ?? 0), 0)

  const activeCount   = orders.filter(o => !['completed','cancelled'].includes(o.status)).length
  const thisMonthKey  = new Date().toISOString().slice(0, 7)
  const thisMonthCount = orders.filter(o => o.event_date?.startsWith(thisMonthKey)).length

  const statuses: CateringStatus[] = [
    'inquiry','quoted','deposit_paid','confirmed','in_production','delivered','completed','cancelled',
  ]

  return (
    <div className="p-6 space-y-5">
      <div className="page-header">
        <h1 className="text-xl font-semibold text-stone-900">Catering Orders & Inquiries</h1>
        <Button size="sm" onClick={() => { setSelected(null); setPanelOpen(true) }}>
          <Plus size={14} />
          New Inquiry
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Orders"    value={orders.length} />
        <StatCard label="Active"          value={activeCount} />
        <StatCard label="Events This Month" value={thisMonthCount} />
        <StatCard label="Completed Revenue" value={`$${completedRevenue.toFixed(0)}`} />
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setStatusFilter(ALL)}
          className={['px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
            statusFilter === ALL ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200',
          ].join(' ')}
        >
          All ({orders.length})
        </button>
        {statuses.map(s => {
          const count = orders.filter(o => o.status === s).length
          if (count === 0) return null
          return (
            <button key={s}
              onClick={() => setStatusFilter(statusFilter === s ? ALL : s)}
              className={['px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                statusFilter === s ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200',
              ].join(' ')}
            >
              {STATUS_LABELS[s]} ({count})
            </button>
          )
        })}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-stone-400 mb-4">No catering orders yet.</p>
          <Button onClick={() => { setSelected(null); setPanelOpen(true) }}>
            <Plus size={14} /> New Inquiry
          </Button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  {['Order #','Client','Event Date','Guests','Quote','Balance','Status',''].map((h, i) => (
                    <th key={i} className={[
                      'px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide',
                      i >= 3 && i <= 5 ? 'text-right' : i === 3 ? 'text-center' : 'text-left',
                    ].join(' ')}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map(order => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onClick={() => { setSelected(order); setPanelOpen(true) }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {panelOpen && (
        <OrderPanel
          order={selected}
          onClose={() => setPanelOpen(false)}
          onSaved={setSelected}
        />
      )}
    </div>
  )
}
